import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  LineChart, 
  Megaphone, 
  Globe2, 
  BarChart2, 
  Layout, 
  Server, 
  Briefcase, 
  ShieldCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CareerProps {
  language?: string;
}

interface JobPosition {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  requirements: string[];
  benefits: string[];
  departmentColor: string;
  department: string;
}

const TRANSLATIONS: Record<string, {
  jobPositions: JobPosition[];
  viewJobDetails: string;
  applyNow: string;
  showMore: string;
  showLess: string;
  jobRequirements: string;
  jobBenefits: string;
  mainHeading: string;
  subHeading: string;
}> = {
  en: {
    mainHeading: "JOIN OUR TEAM",
    subHeading: "Build your career with German engineering excellence",
    jobPositions: [
      {
        icon: Code,
        title: "Software Engineer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Design and develop sophisticated software solutions embodying our 'Made in Germany' precision standards.",
        requirements: [
          "Bachelor's degree in Computer Science or related field",
          "3+ years of experience in software development",
          "Proficiency in Java, Python, or C++",
          "Knowledge of software design patterns and architecture",
          "Experience with agile development methodologies"
        ],
        benefits: [
          "Competitive salary and performance bonuses",
          "Comprehensive health insurance",
          "Professional development opportunities",
          "Flexible work arrangements",
          "Collaborative and innovative work environment"
        ]
      },
      {
        icon: Megaphone,
        title: "Sales Assistant",
        department: "Sales",
        departmentColor: "#0F1627",
        description: "Support our sales team in delivering German-engineered products to global markets with exceptional customer service.",
        requirements: [
          "Previous experience in sales or customer service",
          "Strong communication and interpersonal skills",
          "Proficiency in CRM software",
          "Goal-oriented mindset",
          "Ability to work in a fast-paced environment"
        ],
        benefits: [
          "Commission-based incentives",
          "Career advancement opportunities",
          "Comprehensive training program",
          "Team-building activities",
          "International exposure"
        ]
      },
      {
        icon: Globe2,
        title: "Telecommunications Specialist",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintain and optimize our telecommunications systems with the precision and reliability of German engineering.",
        requirements: [
          "Bachelor's degree in Telecommunications or related field",
          "3+ years of experience in telecommunications",
          "Knowledge of VoIP, network protocols, and infrastructure",
          "Certification in telecommunications systems",
          "Problem-solving and analytical skills"
        ],
        benefits: [
          "Competitive salary package",
          "Continuous learning opportunities",
          "Health and wellness programs",
          "Cutting-edge technology exposure",
          "Remote work options"
        ]
      },
      {
        icon: BarChart2,
        title: "Social Marketing Professionals",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Develop and execute social media strategies that showcase our 'Made in Germany' brand to a global audience.",
        requirements: [
          "Bachelor's degree in Marketing or related field",
          "2+ years of experience in social media marketing",
          "Knowledge of social media platforms and analytics",
          "Creative content creation skills",
          "Understanding of digital marketing trends"
        ],
        benefits: [
          "Competitive compensation",
          "Creative work environment",
          "Professional growth opportunities",
          "Flexible work arrangements",
          "International marketing exposure"
        ]
      },
      {
        icon: LineChart,
        title: "Search-Engine Marketing",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimize our online presence with the precision and efficiency that defines German engineering excellence.",
        requirements: [
          "Experience in SEO/SEM strategies",
          "Knowledge of Google Analytics and AdWords",
          "Understanding of keyword research and optimization",
          "Data analysis and reporting skills",
          "Ability to adapt to algorithm changes"
        ],
        benefits: [
          "Performance-based bonuses",
          "Professional development budget",
          "Team collaboration opportunities",
          "Work-life balance focus",
          "Modern office environment"
        ]
      },
      {
        icon: Briefcase,
        title: "Business Analyst",
        department: "Business",
        departmentColor: "#0F1627",
        description: "Apply analytical precision to business processes, embodying our 'Made in Germany' approach to excellence.",
        requirements: [
          "Bachelor's degree in Business Administration or related field",
          "3+ years of experience in business analysis",
          "Strong analytical and problem-solving skills",
          "Proficiency in data analysis tools",
          "Excellent communication abilities"
        ],
        benefits: [
          "Competitive salary package",
          "Career development opportunities",
          "Healthcare benefits",
          "Work-life balance initiatives",
          "Regular team-building events"
        ]
      },
      {
        icon: Layout,
        title: "Front-End Developer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Create intuitive and efficient user interfaces with the precision and aesthetic quality of German design.",
        requirements: [
          "Experience with modern JavaScript frameworks (React, Vue, Angular)",
          "Strong HTML5 and CSS3 skills",
          "Understanding of responsive design principles",
          "Knowledge of UI/UX best practices",
          "Version control systems experience"
        ],
        benefits: [
          "Competitive compensation",
          "Remote work options",
          "Professional development resources",
          "Collaborative work environment",
          "Latest technology stack"
        ]
      },
      {
        icon: Server,
        title: "Back-End Developer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Develop robust and scalable server-side applications with the reliability that defines German engineering.",
        requirements: [
          "Proficiency in backend languages (Java, Python, Node.js)",
          "Experience with databases (SQL, NoSQL)",
          "Knowledge of RESTful API design",
          "Understanding of server architecture",
          "Problem-solving abilities"
        ],
        benefits: [
          "Competitive salary",
          "Flexible working hours",
          "Health insurance coverage",
          "Professional growth opportunities",
          "Innovative project exposure"
        ]
      },
      {
        icon: Briefcase,
        title: "IT Project Manager",
        department: "Management",
        departmentColor: "#0F1627",
        description: "Lead IT projects with the precision planning and execution that embody our 'Made in Germany' standards.",
        requirements: [
          "Bachelor's degree in IT or related field",
          "5+ years of experience in IT project management",
          "PMP or equivalent certification",
          "Strong leadership and communication skills",
          "Experience with project management methodologies"
        ],
        benefits: [
          "Competitive salary package",
          "Leadership development opportunities",
          "Comprehensive health benefits",
          "Flexible work arrangements",
          "International project exposure"
        ]
      },
      {
        icon: ShieldCheck,
        title: "System Administrator",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintain and secure our IT infrastructure with the reliability and precision of German engineering.",
        requirements: [
          "Bachelor's degree in IT or related field",
          "3+ years of experience in system administration",
          "Knowledge of network security protocols",
          "Experience with cloud services (AWS, Azure, GCP)",
          "Problem-solving abilities"
        ],
        benefits: [
          "Competitive compensation",
          "Professional certification support",
          "Healthcare coverage",
          "Work-life balance focus",
          "Continuous learning opportunities"
        ]
      }
    ],
    viewJobDetails: "View Details",
    applyNow: "Apply Now",
    showMore: "Show More",
    showLess: "Show Less",
    jobRequirements: "Requirements",
    jobBenefits: "Benefits"
  },
  de: {
    mainHeading: "WERDEN SIE TEIL UNSERES TEAMS",
    subHeading: "Gestalten Sie Ihre Karriere mit deutscher Ingenieurskunst",
    jobPositions: [
      {
        icon: Code,
        title: "Softwareingenieur",
        department: "Ingenieurwesen",
        departmentColor: "#0F1627",
        description: "Entwerfen und entwickeln Sie anspruchsvolle Softwarelösungen nach unseren 'Made in Germany' Präzisionsstandards.",
        requirements: [
          "Bachelor-Abschluss in Informatik oder verwandtem Bereich",
          "3+ Jahre Erfahrung in der Softwareentwicklung",
          "Kenntnisse in Java, Python oder C++",
          "Wissen über Software-Designmuster und Architektur",
          "Erfahrung mit agilen Entwicklungsmethoden"
        ],
        benefits: [
          "Wettbewerbsfähiges Gehalt und Leistungsboni",
          "Umfassende Krankenversicherung",
          "Möglichkeiten zur beruflichen Weiterentwicklung",
          "Flexible Arbeitsregelungen",
          "Kollaborative und innovative Arbeitsumgebung"
        ]
      },
      {
        icon: Megaphone,
        title: "Vertriebsassistent",
        department: "Vertrieb",
        departmentColor: "#0F1627",
        description: "Unterstützen Sie unser Vertriebsteam bei der Lieferung deutscher Ingenieurprodukte an globale Märkte mit exzellentem Kundenservice.",
        requirements: [
          "Erfahrung im Vertrieb oder Kundenservice",
          "Starke Kommunikations- und zwischenmenschliche Fähigkeiten",
          "Kenntnisse in CRM-Software",
          "Zielorientierte Denkweise",
          "Fähigkeit, in einem schnelllebigen Umfeld zu arbeiten"
        ],
        benefits: [
          "Provisionsbasierte Anreize",
          "Karriereentwicklungsmöglichkeiten",
          "Umfassendes Schulungsprogramm",
          "Teambuilding-Aktivitäten",
          "Internationale Exposition"
        ]
      },
      {
        icon: Globe2,
        title: "Telekommunikationsspezialist",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Warten und optimieren Sie unsere Telekommunikationssysteme mit der Präzision und Zuverlässigkeit deutscher Ingenieurskunst.",
        requirements: [
          "Bachelor-Abschluss in Telekommunikation oder verwandtem Bereich",
          "3+ Jahre Erfahrung in der Telekommunikation",
          "Kenntnisse in VoIP, Netzwerkprotokollen und Infrastruktur",
          "Zertifizierung in Telekommunikationssystemen",
          "Problemlösungs- und analytische Fähigkeiten"
        ],
        benefits: [
          "Wettbewerbsfähiges Gehaltspaket",
          "Kontinuierliche Lernmöglichkeiten",
          "Gesundheits- und Wellnessprogramme",
          "Zugang zu modernster Technologie",
          "Optionen für Fernarbeit"
        ]
      },
      {
        icon: BarChart2,
        title: "Social Media Marketing Profi",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Entwickeln und setzen Sie Social-Media-Strategien um, die unsere 'Made in Germany' Marke einem globalen Publikum präsentieren.",
        requirements: [
          "Bachelor-Abschluss in Marketing oder verwandtem Bereich",
          "2+ Jahre Erfahrung im Social Media Marketing",
          "Kenntnisse über Social-Media-Plattformen und Analytik",
          "Fähigkeiten zur kreativen Inhaltserstellung",
          "Verständnis für digitale Marketingtrends"
        ],
        benefits: [
          "Wettbewerbsfähige Vergütung",
          "Kreative Arbeitsumgebung",
          "Möglichkeiten zum beruflichen Wachstum",
          "Flexible Arbeitsregelungen",
          "Internationale Marketingexposition"
        ]
      },
      {
        icon: LineChart,
        title: "Suchmaschinen-Marketing",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimieren Sie unsere Online-Präsenz mit der Präzision und Effizienz, die deutsche Ingenieurskunst auszeichnet.",
        requirements: [
          "Erfahrung mit SEO/SEM-Strategien",
          "Kenntnisse in Google Analytics und AdWords",
          "Verständnis für Keyword-Recherche und Optimierung",
          "Datenanalyse- und Berichtsfähigkeiten",
          "Anpassungsfähigkeit an Algorithmusänderungen"
        ],
        benefits: [
          "Leistungsbasierte Boni",
          "Budget für berufliche Weiterentwicklung",
          "Teamzusammenarbeitsmöglichkeiten",
          "Fokus auf Work-Life-Balance",
          "Moderne Büroumgebung"
        ]
      },
      {
        icon: Briefcase,
        title: "Business Analyst",
        department: "Geschäft",
        departmentColor: "#0F1627",
        description: "Wenden Sie analytische Präzision auf Geschäftsprozesse an, die unseren 'Made in Germany' Ansatz für Exzellenz verkörpern.",
        requirements: [
          "Bachelor-Abschluss in Betriebswirtschaft oder verwandtem Bereich",
          "3+ Jahre Erfahrung in der Geschäftsanalyse",
          "Starke analytische und Problemlösungsfähigkeiten",
          "Kenntnisse in Datenanalysetools",
          "Ausgezeichnete Kommunikationsfähigkeiten"
        ],
        benefits: [
          "Wettbewerbsfähiges Gehaltspaket",
          "Karriereentwicklungsmöglichkeiten",
          "Gesundheitsvorteile",
          "Work-Life-Balance-Initiativen",
          "Regelmäßige Teambuilding-Events"
        ]
      },
      {
        icon: Layout,
        title: "Front-End Entwickler",
        department: "Ingenieurwesen",
        departmentColor: "#0F1627",
        description: "Schaffen Sie intuitive und effiziente Benutzeroberflächen mit der Präzision und ästhetischen Qualität deutschen Designs.",
        requirements: [
          "Erfahrung mit modernen JavaScript-Frameworks (React, Vue, Angular)",
          "Starke HTML5- und CSS3-Kenntnisse",
          "Verständnis für Responsive-Design-Prinzipien",
          "Kenntnisse in UI/UX Best Practices",
          "Erfahrung mit Versionskontrollsystemen"
        ],
        benefits: [
          "Wettbewerbsfähige Vergütung",
          "Optionen für Fernarbeit",
          "Ressourcen für berufliche Entwicklung",
          "Kollaborative Arbeitsumgebung",
          "Neuester Technologie-Stack"
        ]
      },
      {
        icon: Server,
        title: "Back-End Entwickler",
        department: "Ingenieurwesen",
        departmentColor: "#0F1627",
        description: "Entwickeln Sie robuste und skalierbare serverseitige Anwendungen mit der Zuverlässigkeit, die deutsche Ingenieurskunst ausmacht.",
        requirements: [
          "Kenntnisse in Backend-Sprachen (Java, Python, Node.js)",
          "Erfahrung mit Datenbanken (SQL, NoSQL)",
          "Wissen über RESTful API-Design",
          "Verständnis der Serverarchitektur",
          "Problemlösungsfähigkeiten"
        ],
        benefits: [
          "Wettbewerbsfähiges Gehalt",
          "Flexible Arbeitszeiten",
          "Krankenversicherungsschutz",
          "Möglichkeiten zum beruflichen Wachstum",
          "Innovative Projektbeteiligung"
        ]
      },
      {
        icon: Briefcase,
        title: "IT-Projektmanager",
        department: "Management",
        departmentColor: "#0F1627",
        description: "Leiten Sie IT-Projekte mit der präzisen Planung und Ausführung, die unsere 'Made in Germany' Standards verkörpern.",
        requirements: [
          "Bachelor-Abschluss in IT oder verwandtem Bereich",
          "5+ Jahre Erfahrung im IT-Projektmanagement",
          "PMP oder gleichwertige Zertifizierung",
          "Starke Führungs- und Kommunikationsfähigkeiten",
          "Erfahrung mit Projektmanagement-Methoden"
        ],
        benefits: [
          "Wettbewerbsfähiges Gehaltspaket",
          "Führungsentwicklungsmöglichkeiten",
          "Umfassende Gesundheitsvorteile",
          "Flexible Arbeitsregelungen",
          "Internationale Projektexposition"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Systemadministrator",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Warten und sichern Sie unsere IT-Infrastruktur mit der Zuverlässigkeit und Präzision deutscher Ingenieurskunst.",
        requirements: [
          "Bachelor-Abschluss in IT oder verwandtem Bereich",
          "3+ Jahre Erfahrung in der Systemadministration",
          "Kenntnisse in Netzwerksicherheitsprotokollen",
          "Erfahrung mit Cloud-Diensten (AWS, Azure, GCP)",
          "Problemlösungsfähigkeiten"
        ],
        benefits: [
          "Wettbewerbsfähige Vergütung",
          "Unterstützung bei beruflichen Zertifizierungen",
          "Gesundheitsversorgung",
          "Fokus auf Work-Life-Balance",
          "Kontinuierliche Lernmöglichkeiten"
        ]
      }
    ],
    viewJobDetails: "Details anzeigen",
    applyNow: "Jetzt bewerben",
    showMore: "Mehr anzeigen",
    showLess: "Weniger anzeigen",
    jobRequirements: "Anforderungen",
    jobBenefits: "Vorteile"
  },
  fr: {
    mainHeading: "REJOIGNEZ NOTRE ÉQUIPE",
    subHeading: "Construisez votre carrière avec l'excellence de l'ingénierie allemande",
    jobPositions: [
      {
        icon: Code,
        title: "Ingénieur Logiciel",
        department: "Ingénierie",
        departmentColor: "#0F1627",
        description: "Concevez et développez des solutions logicielles sophistiquées incarnant nos normes de précision 'Made in Germany'.",
        requirements: [
          "Diplôme de licence en informatique ou domaine connexe",
          "3+ ans d'expérience en développement logiciel",
          "Maîtrise de Java, Python ou C++",
          "Connaissance des modèles de conception logicielle et de l'architecture",
          "Expérience avec les méthodologies de développement agile"
        ],
        benefits: [
          "Salaire compétitif et primes de performance",
          "Assurance santé complète",
          "Opportunités de développement professionnel",
          "Arrangements de travail flexibles",
          "Environnement de travail collaboratif et innovant"
        ]
      },
      {
        icon: Megaphone,
        title: "Assistant Commercial",
        department: "Ventes",
        departmentColor: "#0F1627",
        description: "Soutenez notre équipe de vente dans la livraison de produits conçus en Allemagne aux marchés mondiaux avec un service client exceptionnel.",
        requirements: [
          "Expérience préalable en vente ou service client",
          "Compétences solides en communication et relations interpersonnelles",
          "Maîtrise des logiciels CRM",
          "Esprit orienté vers les objectifs",
          "Capacité à travailler dans un environnement rapide"
        ],
        benefits: [
          "Incitations basées sur les commissions",
          "Opportunités d'avancement de carrière",
          "Programme de formation complet",
          "Activités de renforcement d'équipe",
          "Exposition internationale"
        ]
      },
      {
        icon: Globe2,
        title: "Spécialiste des Télécommunications",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintenez et optimisez nos systèmes de télécommunications avec la précision et la fiabilité de l'ingénierie allemande.",
        requirements: [
          "Diplôme de licence en télécommunications ou domaine connexe",
          "3+ ans d'expérience en télécommunications",
          "Connaissance de VoIP, protocoles réseau et infrastructure",
          "Certification en systèmes de télécommunications",
          "Compétences en résolution de problèmes et analyse"
        ],
        benefits: [
          "Package salarial compétitif",
          "Opportunités d'apprentissage continu",
          "Programmes de santé et de bien-être",
          "Exposition à une technologie de pointe",
          "Options de télétravail"
        ]
      },
      {
        icon: BarChart2,
        title: "Professionnels du Marketing Social",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Développez et exécutez des stratégies de médias sociaux qui mettent en valeur notre marque 'Made in Germany' auprès d'un public mondial.",
        requirements: [
          "Diplôme de licence en marketing ou domaine connexe",
          "2+ ans d'expérience en marketing sur les médias sociaux",
          "Connaissance des plateformes de médias sociaux et de l'analytique",
          "Compétences en création de contenu créatif",
          "Compréhension des tendances du marketing numérique"
        ],
        benefits: [
          "Rémunération compétitive",
          "Environnement de travail créatif",
          "Opportunités de croissance professionnelle",
          "Arrangements de travail flexibles",
          "Exposition au marketing international"
        ]
      },
      {
        icon: LineChart,
        title: "Marketing des Moteurs de Recherche",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimisez notre présence en ligne avec la précision et l'efficacité qui définissent l'excellence de l'ingénierie allemande.",
        requirements: [
          "Expérience en stratégies SEO/SEM",
          "Connaissance de Google Analytics et AdWords",
          "Compréhension de la recherche et de l'optimisation des mots-clés",
          "Compétences en analyse et rapport de données",
          "Capacité à s'adapter aux changements d'algorithmes"
        ],
        benefits: [
          "Primes basées sur la performance",
          "Budget de développement professionnel",
          "Opportunités de collaboration en équipe",
          "Accent sur l'équilibre vie professionnelle/vie personnelle",
          "Environnement de bureau moderne"
        ]
      },
      {
        icon: Briefcase,
        title: "Analyste d'Affaires",
        department: "Affaires",
        departmentColor: "#0F1627",
        description: "Appliquez une précision analytique aux processus commerciaux, incarnant notre approche 'Made in Germany' de l'excellence.",
        requirements: [
          "Diplôme de licence en administration des affaires ou domaine connexe",
          "3+ ans d'expérience en analyse d'affaires",
          "Solides compétences analytiques et de résolution de problèmes",
          "Maîtrise des outils d'analyse de données",
          "Excellentes compétences en communication"
        ],
        benefits: [
          "Package salarial compétitif",
          "Opportunités de développement de carrière",
          "Avantages santé",
          "Initiatives pour l'équilibre vie professionnelle/vie personnelle",
          "Événements réguliers de renforcement d'équipe"
        ]
      },
      {
        icon: Layout,
        title: "Développeur Front-End",
        department: "Ingénierie",
        departmentColor: "#0F1627",
        description: "Créez des interfaces utilisateur intuitives et efficaces avec la précision et la qualité esthétique du design allemand.",
        requirements: [
          "Expérience avec les frameworks JavaScript modernes (React, Vue, Angular)",
          "Solides compétences en HTML5 et CSS3",
          "Compréhension des principes de conception responsive",
          "Connaissance des meilleures pratiques UI/UX",
          "Expérience avec les systèmes de contrôle de version"
        ],
        benefits: [
          "Rémunération compétitive",
          "Options de télétravail",
          "Ressources de développement professionnel",
          "Environnement de travail collaboratif",
          "Pile technologique de pointe"
        ]
      },
      {
        icon: Server,
        title: "Développeur Back-End",
        department: "Ingénierie",
        departmentColor: "#0F1627",
        description: "Développez des applications côté serveur robustes et évolutives avec la fiabilité qui définit l'ingénierie allemande.",
        requirements: [
          "Maîtrise des langages backend (Java, Python, Node.js)",
          "Expérience avec les bases de données (SQL, NoSQL)",
          "Connaissance de la conception d'API RESTful",
          "Compréhension de l'architecture serveur",
          "Compétences en résolution de problèmes"
        ],
        benefits: [
          "Salaire compétitif",
          "Horaires de travail flexibles",
          "Couverture d'assurance santé",
          "Opportunités de croissance professionnelle",
          "Exposition à des projets innovants"
        ]
      },
      {
        icon: Briefcase,
        title: "Gestionnaire de Projet IT",
        department: "Gestion",
        departmentColor: "#0F1627",
        description: "Dirigez des projets IT avec la planification et l'exécution précises qui incarnent nos normes 'Made in Germany'.",
        requirements: [
          "Diplôme de licence en IT ou domaine connexe",
          "5+ ans d'expérience en gestion de projets IT",
          "Certification PMP ou équivalent",
          "Solides compétences en leadership et communication",
          "Expérience avec les méthodologies de gestion de projet"
        ],
        benefits: [
          "Package salarial compétitif",
          "Opportunités de développement en leadership",
          "Avantages santé complets",
          "Arrangements de travail flexibles",
          "Exposition à des projets internationaux"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Administrateur Système",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintenez et sécurisez notre infrastructure IT avec la fiabilité et la précision de l'ingénierie allemande.",
        requirements: [
          "Diplôme de licence en IT ou domaine connexe",
          "3+ ans d'expérience en administration système",
          "Connaissance des protocoles de sécurité réseau",
          "Expérience avec les services cloud (AWS, Azure, GCP)",
          "Compétences en résolution de problèmes"
        ],
        benefits: [
          "Rémunération compétitive",
          "Soutien aux certifications professionnelles",
          "Couverture santé",
          "Accent sur l'équilibre vie professionnelle/vie personnelle",
          "Opportunités d'apprentissage continu"
        ]
      }
    ],
    viewJobDetails: "Voir les détails",
    applyNow: "Postuler maintenant",
    showMore: "Voir plus",
    showLess: "Voir moins",
    jobRequirements: "Exigences",
    jobBenefits: "Avantages"
  },
  es: {
    mainHeading: "ÚNETE A NUESTRO EQUIPO",
    subHeading: "Construye tu carrera con la excelencia de la ingeniería alemana",
    jobPositions: [
      {
        icon: Code,
        title: "Ingeniero de Software",
        department: "Ingeniería",
        departmentColor: "#0F1627",
        description: "Diseña y desarrolla soluciones de software sofisticadas que encarnan nuestros estándares de precisión 'Made in Germany'.",
        requirements: [
          "Licenciatura en Ciencias de la Computación o campo relacionado",
          "3+ años de experiencia en desarrollo de software",
          "Dominio de Java, Python o C++",
          "Conocimiento de patrones de diseño de software y arquitectura",
          "Experiencia con metodologías de desarrollo ágil"
        ],
        benefits: [
          "Salario competitivo y bonos por rendimiento",
          "Seguro de salud integral",
          "Oportunidades de desarrollo profesional",
          "Arreglos de trabajo flexibles",
          "Entorno de trabajo colaborativo e innovador"
        ]
      },
      {
        icon: Megaphone,
        title: "Asistente de Ventas",
        department: "Ventas",
        departmentColor: "#0F1627",
        description: "Apoya a nuestro equipo de ventas en la entrega de productos diseñados en Alemania a mercados globales con un servicio al cliente excepcional.",
        requirements: [
          "Experiencia previa en ventas o servicio al cliente",
          "Fuertes habilidades de comunicación e interpersonales",
          "Dominio de software CRM",
          "Mentalidad orientada a objetivos",
          "Capacidad para trabajar en un entorno acelerado"
        ],
        benefits: [
          "Incentivos basados en comisiones",
          "Oportunidades de avance profesional",
          "Programa de capacitación integral",
          "Actividades de construcción de equipo",
          "Exposición internacional"
        ]
      },
      {
        icon: Globe2,
        title: "Especialista en Telecomunicaciones",
        department: "TI",
        departmentColor: "#0F1627",
        description: "Mantén y optimiza nuestros sistemas de telecomunicaciones con la precisión y fiabilidad de la ingeniería alemana.",
        requirements: [
          "Licenciatura en Telecomunicaciones o campo relacionado",
          "3+ años de experiencia en telecomunicaciones",
          "Conocimiento de VoIP, protocolos de red e infraestructura",
          "Certificación en sistemas de telecomunicaciones",
          "Habilidades de resolución de problemas y análisis"
        ],
        benefits: [
          "Paquete salarial competitivo",
          "Oportunidades de aprendizaje continuo",
          "Programas de salud y bienestar",
          "Exposición a tecnología de vanguardia",
          "Opciones de trabajo remoto"
        ]
      },
      {
        icon: BarChart2,
        title: "Profesionales de Marketing Social",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Desarrolla y ejecuta estrategias de redes sociales que muestren nuestra marca 'Made in Germany' a una audiencia global.",
        requirements: [
          "Licenciatura en Marketing o campo relacionado",
          "2+ años de experiencia en marketing de redes sociales",
          "Conocimiento de plataformas de redes sociales y análisis",
          "Habilidades de creación de contenido creativo",
          "Comprensión de las tendencias de marketing digital"
        ],
        benefits: [
          "Compensación competitiva",
          "Entorno de trabajo creativo",
          "Oportunidades de crecimiento profesional",
          "Arreglos de trabajo flexibles",
          "Exposición al marketing internacional"
        ]
      },
      {
        icon: LineChart,
        title: "Marketing en Motores de Búsqueda",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimiza nuestra presencia en línea con la precisión y eficiencia que define la excelencia de la ingeniería alemana.",
        requirements: [
          "Experiencia en estrategias SEO/SEM",
          "Conocimiento de Google Analytics y AdWords",
          "Comprensión de la investigación y optimización de palabras clave",
          "Habilidades de análisis y reporte de datos",
          "Capacidad para adaptarse a cambios de algoritmos"
        ],
        benefits: [
          "Bonos basados en el rendimiento",
          "Presupuesto de desarrollo profesional",
          "Oportunidades de colaboración en equipo",
          "Enfoque en el equilibrio entre vida laboral y personal",
          "Entorno de oficina moderno"
        ]
      },
      {
        icon: Briefcase,
        title: "Analista de Negocios",
        department: "Negocios",
        departmentColor: "#0F1627",
        description: "Aplica precisión analítica a los procesos de negocio, encarnando nuestro enfoque 'Made in Germany' hacia la excelencia.",
        requirements: [
          "Licenciatura en Administración de Empresas o campo relacionado",
          "3+ años de experiencia en análisis de negocios",
          "Fuertes habilidades analíticas y de resolución de problemas",
          "Dominio de herramientas de análisis de datos",
          "Excelentes habilidades de comunicación"
        ],
        benefits: [
          "Paquete salarial competitivo",
          "Oportunidades de desarrollo profesional",
          "Beneficios de salud",
          "Iniciativas de equilibrio entre vida laboral y personal",
          "Eventos regulares de construcción de equipo"
        ]
      },
      {
        icon: Layout,
        title: "Desarrollador Front-End",
        department: "Ingeniería",
        departmentColor: "#0F1627",
        description: "Crea interfaces de usuario intuitivas y eficientes con la precisión y calidad estética del diseño alemán.",
        requirements: [
          "Experiencia con frameworks modernos de JavaScript (React, Vue, Angular)",
          "Fuertes habilidades en HTML5 y CSS3",
          "Comprensión de los principios de diseño responsivo",
          "Conocimiento de las mejores prácticas de UI/UX",
          "Experiencia con sistemas de control de versiones"
        ],
        benefits: [
          "Compensación competitiva",
          "Opciones de trabajo remoto",
          "Recursos de desarrollo profesional",
          "Entorno de trabajo colaborativo",
          "Pila tecnológica de última generación"
        ]
      },
      {
        icon: Server,
        title: "Desarrollador Back-End",
        department: "Ingeniería",
        departmentColor: "#0F1627",
        description: "Desarrolla aplicaciones robustas y escalables del lado del servidor con la fiabilidad que define la ingeniería alemana.",
        requirements: [
          "Dominio de lenguajes backend (Java, Python, Node.js)",
          "Experiencia con bases de datos (SQL, NoSQL)",
          "Conocimiento del diseño de API RESTful",
          "Comprensión de la arquitectura del servidor",
          "Habilidades de resolución de problemas"
        ],
        benefits: [
          "Salario competitivo",
          "Horarios de trabajo flexibles",
          "Cobertura de seguro de salud",
          "Oportunidades de crecimiento profesional",
          "Exposición a proyectos innovadores"
        ]
      },
      {
        icon: Briefcase,
        title: "Gerente de Proyectos de TI",
        department: "Gestión",
        departmentColor: "#0F1627",
        description: "Dirige proyectos de TI con la planificación y ejecución precisas que encarnan nuestros estándares 'Made in Germany'.",
        requirements: [
          "Licenciatura en TI o campo relacionado",
          "5+ años de experiencia en gestión de proyectos de TI",
          "Certificación PMP o equivalente",
          "Fuertes habilidades de liderazgo y comunicación",
          "Experiencia con metodologías de gestión de proyectos"
        ],
        benefits: [
          "Paquete salarial competitivo",
          "Oportunidades de desarrollo de liderazgo",
          "Beneficios de salud integrales",
          "Arreglos de trabajo flexibles",
          "Exposición a proyectos internacionales"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Administrador de Sistemas",
        department: "TI",
        departmentColor: "#0F1627",
        description: "Mantén y asegura nuestra infraestructura de TI con la fiabilidad y precisión de la ingeniería alemana.",
        requirements: [
          "Licenciatura en TI o campo relacionado",
          "3+ años de experiencia en administración de sistemas",
          "Conocimiento de protocolos de seguridad de red",
          "Experiencia con servicios en la nube (AWS, Azure, GCP)",
          "Habilidades de resolución de problemas"
        ],
        benefits: [
          "Compensación competitiva",
          "Apoyo a certificaciones profesionales",
          "Cobertura de salud",
          "Enfoque en el equilibrio entre vida laboral y personal",
          "Oportunidades de aprendizaje continuo"
        ]
      }
    ],
    viewJobDetails: "Ver detalles",
    applyNow: "Solicitar ahora",
    showMore: "Mostrar más",
    showLess: "Mostrar menos",
    jobRequirements: "Requisitos",
    jobBenefits: "Beneficios"
  },
  it: {
    mainHeading: "UNISCITI AL NOSTRO TEAM",
    subHeading: "Costruisci la tua carriera con l'eccellenza dell'ingegneria tedesca",
    jobPositions: [
      {
        icon: Code,
        title: "Ingegnere del Software",
        department: "Ingegneria",
        departmentColor: "#0F1627",
        description: "Progetta e sviluppa soluzioni software sofisticate che incarnano i nostri standard di precisione 'Made in Germany'.",
        requirements: [
          "Laurea in Informatica o campo correlato",
          "3+ anni di esperienza nello sviluppo software",
          "Competenza in Java, Python o C++",
          "Conoscenza dei modelli di progettazione software e dell'architettura",
          "Esperienza con metodologie di sviluppo agili"
        ],
        benefits: [
          "Stipendio competitivo e bonus per le prestazioni",
          "Assicurazione sanitaria completa",
          "Opportunità di sviluppo professionale",
          "Modalità di lavoro flessibili",
          "Ambiente di lavoro collaborativo e innovativo"
        ]
      },
      {
        icon: Megaphone,
        title: "Assistente alle Vendite",
        department: "Vendite",
        departmentColor: "#0F1627",
        description: "Supporta il nostro team di vendita nella consegna di prodotti progettati in Germania ai mercati globali con un servizio clienti eccezionale.",
        requirements: [
          "Esperienza precedente in vendite o servizio clienti",
          "Forti competenze comunicative e interpersonali",
          "Competenza nei software CRM",
          "Mentalità orientata agli obiettivi",
          "Capacità di lavorare in un ambiente dinamico"
        ],
        benefits: [
          "Incentivi basati su commissioni",
          "Opportunità di avanzamento di carriera",
          "Programma di formazione completo",
          "Attività di team building",
          "Esposizione internazionale"
        ]
      },
      {
        icon: Globe2,
        title: "Specialista in Telecomunicazioni",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Mantieni e ottimizza i nostri sistemi di telecomunicazione con la precisione e l'affidabilità dell'ingegneria tedesca.",
        requirements: [
          "Laurea in Telecomunicazioni o campo correlato",
          "3+ anni di esperienza in telecomunicazioni",
          "Conoscenza di VoIP, protocolli di rete e infrastrutture",
          "Certificazione in sistemi di telecomunicazione",
          "Capacità di problem solving e analisi"
        ],
        benefits: [
          "Pacchetto retributivo competitivo",
          "Opportunità di apprendimento continuo",
          "Programmi di salute e benessere",
          "Esposizione a tecnologie all'avanguardia",
          "Opzioni di lavoro remoto"
        ]
      },
      {
        icon: BarChart2,
        title: "Professionisti del Marketing Sociale",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Sviluppa ed esegui strategie di social media che mostrano il nostro marchio 'Made in Germany' a un pubblico globale.",
        requirements: [
          "Laurea in Marketing o campo correlato",
          "2+ anni di esperienza nel marketing sui social media",
          "Conoscenza delle piattaforme di social media e dell'analitica",
          "Competenze nella creazione di contenuti creativi",
          "Comprensione delle tendenze del marketing digitale"
        ],
        benefits: [
          "Compenso competitivo",
          "Ambiente di lavoro creativo",
          "Opportunità di crescita professionale",
          "Modalità di lavoro flessibili",
          "Esposizione al marketing internazionale"
        ]
      },
      {
        icon: LineChart,
        title: "Marketing sui Motori di Ricerca",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Ottimizza la nostra presenza online con la precisione e l'efficienza che definiscono l'eccellenza dell'ingegneria tedesca.",
        requirements: [
          "Esperienza in strategie SEO/SEM",
          "Conoscenza di Google Analytics e AdWords",
          "Comprensione della ricerca e ottimizzazione delle parole chiave",
          "Competenze di analisi e reporting dei dati",
          "Capacità di adattamento ai cambiamenti degli algoritmi"
        ],
        benefits: [
          "Bonus basati sulle prestazioni",
          "Budget per lo sviluppo professionale",
          "Opportunità di collaborazione in team",
          "Focus sull'equilibrio tra vita lavorativa e personale",
          "Ambiente di ufficio moderno"
        ]
      },
      {
        icon: Briefcase,
        title: "Analista Aziendale",
        department: "Business",
        departmentColor: "#0F1627",
        description: "Applica precisione analitica ai processi aziendali, incarnando il nostro approccio 'Made in Germany' all'eccellenza.",
        requirements: [
          "Laurea in Amministrazione Aziendale o campo correlato",
          "3+ anni di esperienza nell'analisi aziendale",
          "Forti competenze analitiche e di problem solving",
          "Competenza negli strumenti di analisi dei dati",
          "Eccellenti capacità comunicative"
        ],
        benefits: [
          "Pacchetto retributivo competitivo",
          "Opportunità di sviluppo di carriera",
          "Benefici sanitari",
          "Iniziative per l'equilibrio tra vita lavorativa e personale",
          "Eventi regolari di team building"
        ]
      },
      {
        icon: Layout,
        title: "Sviluppatore Front-End",
        department: "Ingegneria",
        departmentColor: "#0F1627",
        description: "Crea interfacce utente intuitive ed efficienti con la precisione e la qualità estetica del design tedesco.",
        requirements: [
          "Esperienza con framework JavaScript moderni (React, Vue, Angular)",
          "Forti competenze in HTML5 e CSS3",
          "Comprensione dei principi di design responsivo",
          "Conoscenza delle migliori pratiche UI/UX",
          "Esperienza con sistemi di controllo di versione"
        ],
        benefits: [
          "Compenso competitivo",
          "Opzioni di lavoro remoto",
          "Risorse per lo sviluppo professionale",
          "Ambiente di lavoro collaborativo",
          "Stack tecnologico all'avanguardia"
        ]
      },
      {
        icon: Server,
        title: "Sviluppatore Back-End",
        department: "Ingegneria",
        departmentColor: "#0F1627",
        description: "Sviluppa applicazioni lato server robuste e scalabili con l'affidabilità che definisce l'ingegneria tedesca.",
        requirements: [
          "Competenza nei linguaggi backend (Java, Python, Node.js)",
          "Esperienza con database (SQL, NoSQL)",
          "Conoscenza del design di API RESTful",
          "Comprensione dell'architettura server",
          "Capacità di problem solving"
        ],
        benefits: [
          "Stipendio competitivo",
          "Orari di lavoro flessibili",
          "Copertura assicurativa sanitaria",
          "Opportunità di crescita professionale",
          "Esposizione a progetti innovativi"
        ]
      },
      {
        icon: Briefcase,
        title: "Project Manager IT",
        department: "Gestione",
        departmentColor: "#0F1627",
        description: "Guida progetti IT con la pianificazione e l'esecuzione precise che incarnano i nostri standard 'Made in Germany'.",
        requirements: [
          "Laurea in IT o campo correlato",
          "5+ anni di esperienza nella gestione di progetti IT",
          "Certificazione PMP o equivalente",
          "Forti competenze di leadership e comunicazione",
          "Esperienza con metodologie di gestione dei progetti"
        ],
        benefits: [
          "Pacchetto retributivo competitivo",
          "Opportunità di sviluppo della leadership",
          "Benefici sanitari completi",
          "Modalità di lavoro flessibili",
          "Esposizione a progetti internazionali"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Amministratore di Sistema",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Mantieni e proteggi la nostra infrastruttura IT con l'affidabilità e la precisione dell'ingegneria tedesca.",
        requirements: [
          "Laurea in IT o campo correlato",
          "3+ anni di esperienza nell'amministrazione di sistema",
          "Conoscenza dei protocolli di sicurezza di rete",
          "Esperienza con servizi cloud (AWS, Azure, GCP)",
          "Capacità di problem solving"
        ],
        benefits: [
          "Compenso competitivo",
          "Supporto per certificazioni professionali",
          "Copertura sanitaria",
          "Focus sull'equilibrio tra vita lavorativa e personale",
          "Opportunità di apprendimento continuo"
        ]
      }
    ],
    viewJobDetails: "Vedi dettagli",
    applyNow: "Candidati ora",
    showMore: "Mostra di più",
    showLess: "Mostra meno",
    jobRequirements: "Requisiti",
    jobBenefits: "Benefici"
  },
  'hk': {
    mainHeading: "加入我哋嘅團隊",
    subHeading: "用德國工程卓越打造你嘅職業生涯",
    jobPositions: [
      {
        icon: Code,
        title: "軟件工程師",
        department: "工程",
        departmentColor: "#0F1627",
        description: "設計同開發複雜嘅軟件解決方案，體現我哋「德國製造」嘅精準標準。",
        requirements: [
          "計算機科學或相關領域嘅學士學位",
          "3年以上軟件開發經驗",
          "精通Java、Python或C++",
          "了解軟件設計模式同架構",
          "有敏捷開發方法經驗"
        ],
        benefits: [
          "具競爭力嘅薪酬同績效獎金",
          "全面健康保險",
          "職業發展機會",
          "靈活嘅工作安排",
          "合作同創新嘅工作環境"
        ]
      },
      {
        icon: Megaphone,
        title: "銷售助理",
        department: "銷售",
        departmentColor: "#0F1627",
        description: "支持我哋嘅銷售團隊，向全球市場交付德國工程產品，提供卓越嘅客戶服務。",
        requirements: [
          "銷售或客戶服務經驗",
          "強大嘅溝通同人際交往能力",
          "熟練使用CRM軟件",
          "目標導向嘅思維方式",
          "能夠喺快節奏環境下工作"
        ],
        benefits: [
          "基於佣金嘅激勵措施",
          "職業晉升機會",
          "全面培訓計劃",
          "團隊建設活動",
          "國際曝光"
        ]
      },
      {
        icon: Globe2,
        title: "電訊專家",
        department: "資訊技術",
        departmentColor: "#0F1627",
        description: "維護同優化我哋嘅電訊系統，體現德國工程嘅精準同可靠性。",
        requirements: [
          "電訊或相關領域嘅學士學位",
          "3年以上電訊經驗",
          "了解VoIP、網絡協議同基礎設施",
          "電訊系統認證",
          "問題解決同分析能力"
        ],
        benefits: [
          "具競爭力嘅薪酬待遇",
          "持續學習機會",
          "健康同福利計劃",
          "接觸尖端技術",
          "遙距工作選項"
        ]
      },
      {
        icon: BarChart2,
        title: "社交營銷專業人員",
        department: "市場營銷",
        departmentColor: "#0F1627",
        description: "制定同執行社交媒體策略，向全球觀眾展示我哋嘅「德國製造」品牌。",
        requirements: [
          "市場營銷或相關領域嘅學士學位",
          "2年以上社交媒體營銷經驗",
          "熟悉社交媒體平台同分析",
          "創意內容創作技能",
          "了解數碼營銷趨勢"
        ],
        benefits: [
          "具競爭力嘅薪酬",
          "創意工作環境",
          "職業成長機會",
          "靈活嘅工作安排",
          "國際營銷曝光"
        ]
      },
      {
        icon: LineChart,
        title: "搜索引擎營銷",
        department: "市場營銷",
        departmentColor: "#0F1627",
        description: "以德國工程卓越嘅精準同效率優化我哋嘅線上存在。",
        requirements: [
          "SEO/SEM策略經驗",
          "熟悉Google Analytics同AdWords",
          "了解關鍵詞研究同優化",
          "數據分析同報告技能",
          "適應算法變化嘅能力"
        ],
        benefits: [
          "基於績效嘅獎金",
          "職業發展預算",
          "團隊合作機會",
          "注重工作同生活平衡",
          "現代化辦公環境"
        ]
      },
      {
        icon: Briefcase,
        title: "業務分析師",
        department: "業務",
        departmentColor: "#0F1627",
        description: "將分析精準應用於業務流程，體現我哋「德國製造」嘅卓越方法。",
        requirements: [
          "工商管理或相關領域嘅學士學位",
          "3年以上業務分析經驗",
          "強大嘅分析同問題解決能力",
          "熟練使用數據分析工具",
          "出色嘅溝通能力"
        ],
        benefits: [
          "具競爭力嘅薪酬待遇",
          "職業發展機會",
          "健康福利",
          "工作同生活平衡措施",
          "定期團隊建設活動"
        ]
      },
      {
        icon: Layout,
        title: "前端開發人員",
        department: "工程",
        departmentColor: "#0F1627",
        description: "以德國設計嘅精準同美學品質創建直觀高效嘅用戶界面。",
        requirements: [
          "熟悉現代JavaScript框架（React、Vue、Angular）",
          "強大嘅HTML5同CSS3技能",
          "理解響應式設計原則",
          "了解UI/UX最佳實踐",
          "版本控制系統經驗"
        ],
        benefits: [
          "具競爭力嘅薪酬",
          "遙距工作選項",
          "職業發展資源",
          "合作工作環境",
          "最新技術堆疊"
        ]
      },
      {
        icon: Server,
        title: "後端開發人員",
        department: "工程",
        departmentColor: "#0F1627",
        description: "開發穩健同可擴展嘅服務器端應用程序，體現德國工程嘅可靠性。",
        requirements: [
          "精通後端語言（Java、Python、Node.js）",
          "數據庫經驗（SQL、NoSQL）",
          "了解RESTful API設計",
          "理解服務器架構",
          "問題解決能力"
        ],
        benefits: [
          "具競爭力嘅薪資",
          "靈活嘅工作時間",
          "健康保險覆蓋",
          "職業成長機會",
          "創新項目曝光"
        ]
      },
      {
        icon: Briefcase,
        title: "資訊技術項目經理",
        department: "管理",
        departmentColor: "#0F1627",
        description: "以「德國製造」標準嘅精準計劃同執行領導資訊技術項目。",
        requirements: [
          "資訊技術或相關領域嘅學士學位",
          "5年以上資訊技術項目管理經驗",
          "PMP或同等認證",
          "強大嘅領導同溝通能力",
          "項目管理方法經驗"
        ],
        benefits: [
          "具競爭力嘅薪酬待遇",
          "領導力發展機會",
          "全面健康福利",
          "靈活嘅工作安排",
          "國際項目曝光"
        ]
      },
      {
        icon: ShieldCheck,
        title: "系統管理員",
        department: "資訊技術",
        departmentColor: "#0F1627",
        description: "以德國工程嘅可靠性同精準性維護同保護我哋嘅資訊技術基礎設施。",
        requirements: [
          "資訊技術或相關領域嘅學士學位",
          "3年以上系統管理經驗",
          "了解網絡安全協議",
          "雲端服務經驗（AWS、Azure、GCP）",
          "問題解決能力"
        ],
        benefits: [
          "具競爭力嘅薪酬",
          "職業認證支持",
          "健康保險",
          "注重工作同生活平衡",
          "持續學習機會"
        ]
      }
    ],
    viewJobDetails: "查看詳情",
    applyNow: "立即申請",
    showMore: "顯示更多",
    showLess: "顯示更少",
    jobRequirements: "要求",
    jobBenefits: "福利"
  },
  'sg': {
    mainHeading: "JOIN OUR TEAM",
    subHeading: "Build your career with German engineering excellence",
    jobPositions: [
      {
        icon: Code,
        title: "Software Engineer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Design and develop sophisticated software solutions embodying our 'Made in Germany' precision standards.",
        requirements: [
          "Bachelor's degree in Computer Science or related field",
          "3+ years of experience in software development",
          "Proficiency in Java, Python, or C++",
          "Knowledge of software design patterns and architecture",
          "Experience with agile development methodologies"
        ],
        benefits: [
          "Competitive salary and performance bonuses",
          "Comprehensive health insurance",
          "Professional development opportunities",
          "Flexible work arrangements",
          "Collaborative and innovative work environment"
        ]
      },
      {
        icon: Megaphone,
        title: "Sales Assistant",
        department: "Sales",
        departmentColor: "#0F1627",
        description: "Support our sales team in delivering German-engineered products to global markets with exceptional customer service.",
        requirements: [
          "Previous experience in sales or customer service",
          "Strong communication and interpersonal skills",
          "Proficiency in CRM software",
          "Goal-oriented mindset",
          "Ability to work in a fast-paced environment"
        ],
        benefits: [
          "Commission-based incentives",
          "Career advancement opportunities",
          "Comprehensive training programme",
          "Team-building activities",
          "International exposure"
        ]
      },
      {
        icon: Globe2,
        title: "Telecommunications Specialist",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintain and optimise our telecommunications systems with the precision and reliability of German engineering.",
        requirements: [
          "Bachelor's degree in Telecommunications or related field",
          "3+ years of experience in telecommunications",
          "Knowledge of VoIP, network protocols, and infrastructure",
          "Certification in telecommunications systems",
          "Problem-solving and analytical skills"
        ],
        benefits: [
          "Competitive salary package",
          "Continuous learning opportunities",
          "Health and wellness programmes",
          "Exposure to cutting-edge technology",
          "Remote work options"
        ]
      },
      {
        icon: BarChart2,
        title: "Social Marketing Professional",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Develop and execute social media strategies that showcase our 'Made in Germany' brand to a global audience.",
        requirements: [
          "Bachelor's degree in Marketing or related field",
          "2+ years of experience in social media marketing",
          "Knowledge of social media platforms and analytics",
          "Creative content creation skills",
          "Understanding of digital marketing trends"
        ],
        benefits: [
          "Competitive remuneration",
          "Creative work environment",
          "Professional growth opportunities",
          "Flexible work arrangements",
          "International marketing exposure"
        ]
      },
      {
        icon: LineChart,
        title: "Search Engine Marketing Specialist",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimise our online presence with the precision and efficiency that defines German engineering excellence.",
        requirements: [
          "Experience in SEO/SEM strategies",
          "Knowledge of Google Analytics and AdWords",
          "Understanding of keyword research and optimisation",
          "Data analysis and reporting skills",
          "Ability to adapt to algorithm changes"
        ],
        benefits: [
          "Performance-based bonuses",
          "Professional development budget",
          "Team collaboration opportunities",
          "Focus on work-life balance",
          "Modern office environment"
        ]
      },
      {
        icon: Briefcase,
        title: "Business Analyst",
        department: "Business",
        departmentColor: "#0F1627",
        description: "Apply analytical precision to business processes, embodying our 'Made in Germany' approach to excellence.",
        requirements: [
          "Bachelor's degree in Business Administration or related field",
          "3+ years of experience in business analysis",
          "Strong analytical and problem-solving skills",
          "Proficiency in data analysis tools",
          "Excellent communication abilities"
        ],
        benefits: [
          "Competitive salary package",
          "Career development opportunities",
          "Healthcare benefits",
          "Work-life balance initiatives",
          "Regular team-building events"
        ]
      },
      {
        icon: Layout,
        title: "Front-End Developer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Create intuitive and efficient user interfaces with the precision and aesthetic quality of German design.",
        requirements: [
          "Experience with modern JavaScript frameworks (React, Vue, Angular)",
          "Strong HTML5 and CSS3 skills",
          "Understanding of responsive design principles",
          "Knowledge of UI/UX best practices",
          "Version control systems experience"
        ],
        benefits: [
          "Competitive compensation",
          "Remote work options",
          "Professional development resources",
          "Collaborative work environment",
          "Latest technology stack"
        ]
      },
      {
        icon: Server,
        title: "Back-End Developer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Develop robust and scalable server-side applications with the reliability that defines German engineering.",
        requirements: [
          "Proficiency in backend languages (Java, Python, Node.js)",
          "Experience with databases (SQL, NoSQL)",
          "Knowledge of RESTful API design",
          "Understanding of server architecture",
          "Problem-solving abilities"
        ],
        benefits: [
          "Competitive salary",
          "Flexible working hours",
          "Health insurance coverage",
          "Professional growth opportunities",
          "Innovative project exposure"
        ]
      },
      {
        icon: Briefcase,
        title: "IT Project Manager",
        department: "Management",
        departmentColor: "#0F1627",
        description: "Lead IT projects with the precision planning and execution that embody our 'Made in Germany' standards.",
        requirements: [
          "Bachelor's degree in IT or related field",
          "5+ years of experience in IT project management",
          "PMP or equivalent certification",
          "Strong leadership and communication skills",
          "Experience with project management methodologies"
        ],
        benefits: [
          "Competitive salary package",
          "Leadership development opportunities",
          "Comprehensive health benefits",
          "Flexible work arrangements",
          "International project exposure"
        ]
      },
      {
        icon: ShieldCheck,
        title: "System Administrator",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Maintain and secure our IT infrastructure with the reliability and precision of German engineering.",
        requirements: [
          "Bachelor's degree in IT or related field",
          "3+ years of experience in system administration",
          "Knowledge of network security protocols",
          "Experience with cloud services (AWS, Azure, GCP)",
          "Problem-solving abilities"
        ],
        benefits: [
          "Competitive compensation",
          "Professional certification support",
          "Healthcare coverage",
          "Focus on work-life balance",
          "Continuous learning opportunities"
        ]
      }
    ],
    viewJobDetails: "View Details",
    applyNow: "Apply Now",
    showMore: "Show More",
    showLess: "Show Less",
    jobRequirements: "Requirements",
    jobBenefits: "Benefits"
  },
  'za': {
    mainHeading: "SLUIT BY ONS SPAN AAN",
    subHeading: "Bou jou loopbaan met Duitse ingenieursuitnemendheid",
    jobPositions: [
      {
        icon: Code,
        title: "Sagteware-ingenieur",
        department: "Ingenieurswese",
        departmentColor: "#0F1627",
        description: "Ontwerp en ontwikkel gesofistikeerde sagteware-oplossings wat ons 'Gemaak in Duitsland' presisiestandaarde beliggaam.",
        requirements: [
          "Baccalaureusgraad in Rekenaarwetenskap of verwante veld",
          "3+ jaar ondervinding in sagteware-ontwikkeling",
          "Vaardigheid in Java, Python, of C++",
          "Kennis van sagteware-ontwerppatrone en argitektuur",
          "Ervaring met ratse ontwikkelingsmetodologieë"
        ],
        benefits: [
          "Mededingende salaris en prestasiebonusse",
          "Omvattende gesondheidsversekering",
          "Professionele ontwikkelingsgeleenthede",
          "Buigsame werksreëlings",
          "Samewerkende en innoverende werksomgewing"
        ]
      },
      {
        icon: Megaphone,
        title: "Verkoopsassistent",
        department: "Verkope",
        departmentColor: "#0F1627",
        description: "Ondersteun ons verkoopspan om Duitse ontwerpte produkte aan globale markte te lewer met uitsonderlike kliëntediens.",
        requirements: [
          "Vorige ondervinding in verkope of kliëntediens",
          "Sterk kommunikasie- en interpersoonlike vaardighede",
          "Vaardigheid in CRM-sagteware",
          "Doelgerigte ingesteldheid",
          "Vermoeë om in 'n vinnige omgewing te werk"
        ],
        benefits: [
          "Kommissiegebaseerde aansporings",
          "Loopbaanvorderingsgeleenthede",
          "Omvattende opleidingsprogram",
          "Spanbou-aktiwiteite",
          "Internasionale blootstelling"
        ]
      },
      {
        icon: Globe2,
        title: "Telekommunikasie-spesialis",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Onderhou en optimaliseer ons telekommunikasie-stelsels met die presisie en betroubaarheid van Duitse ingenieurswese.",
        requirements: [
          "Baccalaureusgraad in Telekommunikasie of verwante veld",
          "3+ jaar ondervinding in telekommunikasie",
          "Kennis van VoIP, netwerkprotokolle en infrastruktuur",
          "Sertifisering in telekommunikasie-stelsels",
          "Probleemoplossing- en analitiese vaardighede"
        ],
        benefits: [
          "Mededingende salaris pakket",
          "Deurlopende leergeleenthede",
          "Gesondheids- en welstandsprogramme",
          "Blootstelling aan voorpunt-tegnologie",
          "Afstandswerk-opsies"
        ]
      },
      {
        icon: BarChart2,
        title: "Sosiale Bemarkingsprofessioneel",
        department: "Bemarking",
        departmentColor: "#0F1627",
        description: "Ontwikkel en voer sosiale media-strategieë uit wat ons 'Gemaak in Duitsland' handelsmerk aan 'n wêreldwye gehoor vertoon.",
        requirements: [
          "Baccalaureusgraad in Bemarking of verwante veld",
          "2+ jaar ondervinding in sosiale media-bemarking",
          "Kennis van sosiale media-platforms en analitika",
          "Kreatiewe inhoudskeppingsvaardighede",
          "Begrip van digitale bemarkingstendense"
        ],
        benefits: [
          "Mededingende vergoeding",
          "Kreatiewe werksomgewing",
          "Professionele groeigeleenthede",
          "Buigsame werksreëlings",
          "Internasionale bemarkingsblootstelling"
        ]
      },
      {
        icon: LineChart,
        title: "Soekenjinbemarking",
        department: "Bemarking",
        departmentColor: "#0F1627",
        description: "Optimaliseer ons aanlyn-teenwoordigheid met die presisie en doeltreffendheid wat Duitse ingenieursuitnemendheid definieer.",
        requirements: [
          "Ervaring in SEO/SEM-strategieë",
          "Kennis van Google Analytics en AdWords",
          "Begrip van sleutelwoordnavorsing en optimalisering",
          "Data-analise en verslagdoeningsvaardighede",
          "Vermoeë om aan te pas by algoritme-veranderinge"
        ],
        benefits: [
          "Prestasiegebaseerde bonusse",
          "Professionele ontwikkelingsbegroting",
          "Span-samewerkingsgeleenthede",
          "Fokus op werk-lewe-balans",
          "Moderne kantooromgewing"
        ]
      },
      {
        icon: Briefcase,
        title: "Besigheidsontleder",
        department: "Besigheid",
        departmentColor: "#0F1627",
        description: "Pas analitiese presisie toe op besigheidsprosesse, wat ons 'Gemaak in Duitsland' benadering tot uitnemendheid beliggaam.",
        requirements: [
          "Baccalaureusgraad in Besigheidsadministrasie of verwante veld",
          "3+ jaar ondervinding in besigheidsanalise",
          "Sterk analitiese en probleemoplossingsvaardighede",
          "Vaardigheid in data-analise gereedskap",
          "Uitstekende kommunikasievermoëns"
        ],
        benefits: [
          "Mededingende salaris pakket",
          "Loopbaan-ontwikkelingsgeleenthede",
          "Gesondheidsvoordele",
          "Werk-lewe-balans inisiatiewe",
          "Gereelde spanbou-geleenthede"
        ]
      },
      {
        icon: Layout,
        title: "Vooraf-ontwikkelaar",
        department: "Ingenieurswese",
        departmentColor: "#0F1627",
        description: "Skep intuïtiewe en doeltreffende gebruikerskoppelvlakke met die presisie en estetiese kwaliteit van Duitse ontwerp.",
        requirements: [
          "Ervaring met moderne JavaScript-raamwerke (React, Vue, Angular)",
          "Sterk HTML5 en CSS3 vaardighede",
          "Begrip van responsiewe ontwerp beginsels",
          "Kennis van UI/UX beste praktyke",
          "Weergawebeheerstelsels ondervinding"
        ],
        benefits: [
          "Mededingende vergoeding",
          "Afstandswerk-opsies",
          "Professionele ontwikkelingshulpbronne",
          "Samewerkende werksomgewing",
          "Nuutste tegnologie stapel"
        ]
      },
      {
        icon: Server,
        title: "Agterkant-ontwikkelaar",
        department: "Ingenieurswese",
        departmentColor: "#0F1627",
        description: "Ontwikkel robuuste en skaalbare bedienerkant-toepassings met die betroubaarheid wat Duitse ingenieurswese definieer.",
        requirements: [
          "Vaardigheid in agterkant-tale (Java, Python, Node.js)",
          "Ervaring met databasisse (SQL, NoSQL)",
          "Kennis van RESTful API-ontwerp",
          "Begrip van bedienerargitektuur",
          "Probleemoplossingsvermoëns"
        ],
        benefits: [
          "Mededingende salaris",
          "Buigsame werksure",
          "Gesondheidsversekeringsdekking",
          "Professionele groeigeleenthede",
          "Innoverende projekblootstelling"
        ]
      },
      {
        icon: Briefcase,
        title: "IT-projekbestuurder",
        department: "Bestuur",
        departmentColor: "#0F1627",
        description: "Lei IT-projekte met die presiese beplanning en uitvoering wat ons 'Gemaak in Duitsland' standaarde beliggaam.",
        requirements: [
          "Baccalaureusgraad in IT of verwante veld",
          "5+ jaar ondervinding in IT-projekbestuur",
          "PMP of ekwivalente sertifisering",
          "Sterk leierskap- en kommunikasievaardighede",
          "Ervaring met projekbestuurmetodologieë"
        ],
        benefits: [
          "Mededingende salaris pakket",
          "Leierskap-ontwikkelingsgeleenthede",
          "Omvattende gesondheidsvoordele",
          "Buigsame werksreëlings",
          "Internasionale projekblootstelling"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Stelseladministrateur",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Onderhou en beveilig ons IT-infrastruktuur met die betroubaarheid en presisie van Duitse ingenieurswese.",
        requirements: [
          "Baccalaureusgraad in IT of verwante veld",
          "3+ jaar ondervinding in stelseladministrasie",
          "Kennis van netwerksekuriteitsprotokolle",
          "Ervaring met wolkdienste (AWS, Azure, GCP)",
          "Probleemoplossingsvermoëns"
        ],
        benefits: [
          "Mededingende vergoeding",
          "Professionele sertifiseringsondersteuning",
          "Gesondheidsdekking",
          "Fokus op werk-lewe-balans",
          "Deurlopende leergeleenthede"
        ]
      }
    ],
    viewJobDetails: "Sien Besonderhede",
    applyNow: "Solliciteer Nou",
    showMore: "Wys Meer",
    showLess: "Wys Minder",
    jobRequirements: "Vereistes",
    jobBenefits: "Voordele"
  },
  nl: {
    mainHeading: "SLUIT JE AAN BIJ ONS TEAM",
    subHeading: "Bouw je carrière op met Duitse ingenieursuitmuntendheid",
    jobPositions: [
      {
        icon: Code,
        title: "Software Engineer",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Ontwerp en ontwikkel geavanceerde softwareoplossingen die onze 'Made in Germany' precisienormen belichamen.",
        requirements: [
          "Bachelor in Computerwetenschappen of gerelateerd veld",
          "3+ jaar ervaring in softwareontwikkeling",
          "Vaardigheid in Java, Python of C++",
          "Kennis van softwareontwerppatronen en architectuur",
          "Ervaring met agile ontwikkelingsmethodologieën"
        ],
        benefits: [
          "Competitief salaris en prestatiebonussen",
          "Uitgebreide zorgverzekering",
          "Mogelijkheden voor professionele ontwikkeling",
          "Flexibele werkregelingen",
          "Samenwerkende en innovatieve werkomgeving"
        ]
      },
      {
        icon: Megaphone,
        title: "Verkoopassistent",
        department: "Verkoop",
        departmentColor: "#0F1627",
        description: "Ondersteun ons verkoopteam bij het leveren van in Duitsland ontworpen producten aan wereldmarkten met uitstekende klantenservice.",
        requirements: [
          "Eerdere ervaring in verkoop of klantenservice",
          "Sterke communicatie- en interpersoonlijke vaardigheden",
          "Vaardigheid in CRM-software",
          "Doelgerichte mentaliteit",
          "Vermogen om te werken in een snelle omgeving"
        ],
        benefits: [
          "Commissie-gebaseerde incentieven",
          "Carrièregroeimogelijkheden",
          "Uitgebreid trainingsprogramma",
          "Teambuilding-activiteiten",
          "Internationale exposure"
        ]
      },
      {
        icon: Globe2,
        title: "Telecommunicatiespecialist",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Onderhoud en optimaliseer onze telecommunicatiesystemen met de precisie en betrouwbaarheid van Duitse engineering.",
        requirements: [
          "Bachelor in Telecommunicatie of gerelateerd veld",
          "3+ jaar ervaring in telecommunicatie",
          "Kennis van VoIP, netwerkprotocollen en infrastructuur",
          "Certificering in telecommunicatiesystemen",
          "Probleemoplossende en analytische vaardigheden"
        ],
        benefits: [
          "Competitief salarispakket",
          "Continue leermogelijkheden",
          "Gezondheids- en welzijnsprogramma's",
          "Blootstelling aan geavanceerde technologie",
          "Opties voor thuiswerken"
        ]
      },
      {
        icon: BarChart2,
        title: "Sociale Marketing Professionals",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Ontwikkel en voer sociale mediastrategieën uit die ons 'Made in Germany' merk aan een wereldwijd publiek tonen.",
        requirements: [
          "Bachelor in Marketing of gerelateerd veld",
          "2+ jaar ervaring in sociale mediamarketing",
          "Kennis van sociale mediaplatforms en analyses",
          "Creatieve vaardigheden voor contentcreatie",
          "Begrip van digitale marketingtrends"
        ],
        benefits: [
          "Competitieve vergoeding",
          "Creatieve werkomgeving",
          "Mogelijkheden voor professionele groei",
          "Flexibele werkregelingen",
          "Internationale marketingexposure"
        ]
      },
      {
        icon: LineChart,
        title: "Zoekmachine Marketing",
        department: "Marketing",
        departmentColor: "#0F1627",
        description: "Optimaliseer onze online aanwezigheid met de precisie en efficiëntie die Duitse ingenieursuitmuntendheid definieert.",
        requirements: [
          "Ervaring met SEO/SEM-strategieën",
          "Kennis van Google Analytics en AdWords",
          "Begrip van zoekwoordonderzoek en optimalisatie",
          "Data-analyse en rapportagevaardigheden",
          "Vermogen om zich aan te passen aan algoritmeveranderingen"
        ],
        benefits: [
          "Prestatiegebaseerde bonussen",
          "Budget voor professionele ontwikkeling",
          "Samenwerkingsmogelijkheden in teams",
          "Focus op werk-privébalans",
          "Moderne kantooromgeving"
        ]
      },
      {
        icon: Briefcase,
        title: "Bedrijfsanalist",
        department: "Bedrijf",
        departmentColor: "#0F1627",
        description: "Pas analytische precisie toe op bedrijfsprocessen, die onze 'Made in Germany' benadering van uitmuntendheid belichamen.",
        requirements: [
          "Bachelor in Bedrijfskunde of gerelateerd veld",
          "3+ jaar ervaring in bedrijfsanalyse",
          "Sterke analytische en probleemoplossende vaardigheden",
          "Vaardigheid in data-analysetools",
          "Uitstekende communicatieve vaardigheden"
        ],
        benefits: [
          "Competitief salarispakket",
          "Carrière-ontwikkelingsmogelijkheden",
          "Gezondheidsvoordelen",
          "Werk-privébalans initiatieven",
          "Regelmatige teambuilding-evenementen"
        ]
      },
      {
        icon: Layout,
        title: "Front-End Ontwikkelaar",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Creëer intuïtieve en efficiënte gebruikersinterfaces met de precisie en esthetische kwaliteit van Duits design.",
        requirements: [
          "Ervaring met moderne JavaScript-frameworks (React, Vue, Angular)",
          "Sterke HTML5- en CSS3-vaardigheden",
          "Begrip van responsieve ontwerpprincipes",
          "Kennis van UI/UX best practices",
          "Ervaring met versiecontrolesystemen"
        ],
        benefits: [
          "Competitieve vergoeding",
          "Opties voor thuiswerken",
          "Middelen voor professionele ontwikkeling",
          "Samenwerkende werkomgeving",
          "Nieuwste technologie-stack"
        ]
      },
      {
        icon: Server,
        title: "Back-End Ontwikkelaar",
        department: "Engineering",
        departmentColor: "#0F1627",
        description: "Ontwikkel robuuste en schaalbare server-side applicaties met de betrouwbaarheid die Duitse engineering definieert.",
        requirements: [
          "Vaardigheid in backend-talen (Java, Python, Node.js)",
          "Ervaring met databases (SQL, NoSQL)",
          "Kennis van RESTful API-ontwerp",
          "Begrip van serverarchitectuur",
          "Probleemoplossende vaardigheden"
        ],
        benefits: [
          "Competitief salaris",
          "Flexibele werktijden",
          "Zorgverzekeringsdekking",
          "Mogelijkheden voor professionele groei",
          "Innovatieve projectexposure"
        ]
      },
      {
        icon: Briefcase,
        title: "IT Projectmanager",
        department: "Management",
        departmentColor: "#0F1627",
        description: "Leid IT-projecten met de precieze planning en uitvoering die onze 'Made in Germany' normen belichamen.",
        requirements: [
          "Bachelor in IT of gerelateerd veld",
          "5+ jaar ervaring in IT-projectmanagement",
          "PMP of gelijkwaardige certificering",
          "Sterke leiderschaps- en communicatieve vaardigheden",
          "Ervaring met projectmanagementmethodologieën"
        ],
        benefits: [
          "Competitief salarispakket",
          "Leiderschapsontwikkelingsmogelijkheden",
          "Uitgebreide gezondheidsvoordelen",
          "Flexibele werkregelingen",
          "Internationale projectexposure"
        ]
      },
      {
        icon: ShieldCheck,
        title: "Systeembeheerder",
        department: "IT",
        departmentColor: "#0F1627",
        description: "Onderhoud en beveilig onze IT-infrastructuur met de betrouwbaarheid en precisie van Duitse engineering.",
        requirements: [
          "Bachelor in IT of gerelateerd veld",
          "3+ jaar ervaring in systeembeheer",
          "Kennis van netwerkbeveiligingsprotocollen",
          "Ervaring met clouddiensten (AWS, Azure, GCP)",
          "Probleemoplossende vaardigheden"
        ],
        benefits: [
          "Competitieve vergoeding",
          "Ondersteuning voor professionele certificeringen",
          "Gezondheidsdekking",
          "Focus op werk-privébalans",
          "Continue leermogelijkheden"
        ]
      }
    ],
    viewJobDetails: "Bekijk details",
    applyNow: "Solliciteer nu",
    showMore: "Toon meer",
    showLess: "Toon minder",
    jobRequirements: "Vereisten",
    jobBenefits: "Voordelen"
  },
  zh: {
    mainHeading: "加入我们的团队",
    subHeading: "用德国工程卓越打造你的职业生涯",
    jobPositions: [
      {
        icon: Code,
        title: "软件工程师",
        department: "工程",
        departmentColor: "#0F1627",
        description: "设计和开发复杂的软件解决方案，体现我们“德国制造”的精确标准。",
        requirements: [
          "计算机科学或相关领域的学士学位",
          "3年以上软件开发经验",
          "精通Java、Python或C++",
          "了解软件设计模式和架构",
          "有敏捷开发方法经验"
        ],
        benefits: [
          "有竞争力的薪资和绩效奖金",
          "全面的健康保险",
          "职业发展机会",
          "灵活的工作安排",
          "协作和创新的工作环境"
        ]
      },
      {
        icon: Megaphone,
        title: "销售助理",
        department: "销售",
        departmentColor: "#0F1627",
        description: "支持我们的销售团队向全球市场交付德国工程产品，提供卓越的客户服务。",
        requirements: [
          "销售或客户服务经验",
          "强大的沟通和人际交往能力",
          "熟练使用CRM软件",
          "目标导向的思维方式",
          "能在快节奏环境中工作"
        ],
        benefits: [
          "基于佣金的激励措施",
          "职业晋升机会",
          "全面的培训计划",
          "团队建设活动",
          "国际曝光"
        ]
      },
      {
        icon: Globe2,
        title: "电信专家",
        department: "信息技术",
        departmentColor: "#0F1627",
        description: "维护和优化我们的电信系统，体现德国工程的精确性和可靠性。",
        requirements: [
          "电信或相关领域的学士学位",
          "3年以上电信经验",
          "了解VoIP、网络协议和基础设施",
          "电信系统认证",
          "问题解决和分析能力"
        ],
        benefits: [
          "有竞争力的薪资待遇",
          "持续学习机会",
          "健康和福利计划",
          "接触尖端技术",
          "远程工作选项"
        ]
      },
      {
        icon: BarChart2,
        title: "社交营销专业人员",
        department: "市场营销",
        departmentColor: "#0F1627",
        description: "制定并执行社交媒体策略，向全球观众展示我们的“德国制造”品牌。",
        requirements: [          "市场营销或相关领域的学士学位",
          "2年以上社交媒体营销经验",
          "熟悉社交媒体平台和分析",
          "创意内容创作技能",
          "了解数字营销趋势"
        ],
        benefits: [
          "有竞争力的薪酬",
          "创意工作环境",
          "职业成长机会",
          "灵活的工作安排",
          "国际营销曝光"
        ]
      },
      {
        icon: LineChart,
        title: "搜索引擎营销",
        department: "市场营销",
        departmentColor: "#0F1627",
        description: "以德国工程卓越的精确性和效率优化我们的在线存在。",
        requirements: [
          "SEO/SEM策略经验",
          "熟悉Google Analytics和AdWords",
          "了解关键词研究和优化",
          "数据分析和报告技能",
          "适应算法变化的能力"
        ],
        benefits: [
          "基于绩效的奖金",
          "职业发展预算",
          "团队合作机会",
          "注重工作与生活的平衡",
          "现代化办公环境"
        ]
      },
      {
        icon: Briefcase,
        title: "业务分析师",
        department: "业务",
        departmentColor: "#0F1627",
        description: "将分析精确性应用于业务流程，体现我们“德国制造”的卓越方法。",
        requirements: [
          "工商管理或相关领域的学士学位",
          "3年以上业务分析经验",
          "强大的分析和问题解决能力",
          "熟练使用数据分析工具",
          "出色的沟通能力"
        ],
        benefits: [
          "有竞争力的薪资待遇",
          "职业发展机会",
          "健康福利",
          "工作与生活平衡举措",
          "定期团队建设活动"
        ]
      },
      {
        icon: Layout,
        title: "前端开发人员",
        department: "工程",
        departmentColor: "#0F1627",
        description: "以德国设计的精确性和美学品质创建直观高效的用户界面。",
        requirements: [
          "熟悉现代JavaScript框架（React、Vue、Angular）",
          "强大的HTML5和CSS3技能",
          "理解响应式设计原则",
          "了解UI/UX最佳实践",
          "版本控制系统经验"
        ],
        benefits: [
          "有竞争力的薪酬",
          "远程工作选项",
          "职业发展资源",
          "协作工作环境",
          "最新技术栈"
        ]
      },
      {
        icon: Server,
        title: "后端开发人员",
        department: "工程",
        departmentColor: "#0F1627",
        description: "开发稳健且可扩展的服务器端应用程序，体现德国工程的可靠性。",
        requirements: [
          "精通后端语言（Java、Python、Node.js）",
          "数据库经验（SQL、NoSQL）",
          "了解RESTful API设计",
          "理解服务器架构",
          "问题解决能力"
        ],
        benefits: [
          "有竞争力的薪资",
          "灵活的工作时间",
          "健康保险覆盖",
          "职业成长机会",
          "创新项目曝光"
        ]
      },
      {
        icon: Briefcase,
        title: "IT项目经理",
        department: "管理",
        departmentColor: "#0F1627",
        description: "以“德国制造”标准的精确计划和执行领导IT项目。",
        requirements: [
          "IT或相关领域的学士学位",
          "5年以上IT项目管理经验",
          "PMP或同等认证",
          "强大的领导和沟通能力",
          "项目管理方法经验"
        ],
        benefits: [
          "有竞争力的薪资待遇",
          "领导力发展机会",
          "全面健康福利",
          "灵活的工作安排",
          "国际项目曝光"
        ]
      },
      {
        icon: ShieldCheck,
        title: "系统管理员",
        department: "信息技术",
        departmentColor: "#0F1627",
        description: "以德国工程的可靠性和精确性维护和保护我们的IT基础设施。",
        requirements: [
          "IT或相关领域的学士学位",
          "3年以上系统管理经验",
          "了解网络安全协议",
          "云计算服务经验（AWS、Azure、GCP）",
          "问题解决能力"
        ],
        benefits: [
          "有竞争力的薪酬",
          "职业认证支持",
          "健康保险",
          "注重工作与生活平衡",
          "持续学习机会"
        ]
      }
    ],
    viewJobDetails: "查看详情",
    applyNow: "立即申请",
    showMore: "显示更多",
    showLess: "显示更少",
    jobRequirements: "要求",
    jobBenefits: "福利"
  },
  jp: {
    mainHeading: "私たちのチームに参加",
    subHeading: "ドイツのエンジニアリングの卓越性でキャリアを築く",
    jobPositions: [
      {
        icon: Code,
        title: "ソフトウェアエンジニア",
        department: "エンジニアリング",
        departmentColor: "#0F1627",
        description: "「メイド・イン・ジャーマニー」の精密基準を体現する洗練されたソフトウェアソリューションを設計・開発する。",
        requirements: [
          "コンピュータサイエンスまたは関連分野の学士号",
          "ソフトウェア開発における3年以上の経験",
          "Java、Python、またはC++の熟練",
          "ソフトウェア設計パターンとアーキテクチャの知識",
          "アジャイル開発手法の経験"
        ],
        benefits: [
          "競争力のある給与とパフォーマンスボーナス",
          "包括的な健康保険",
          "専門能力開発の機会",
          "柔軟な勤務形態",
          "協力的で革新的な職場環境"
        ]
      },
      {
        icon: Megaphone,
        title: "営業アシスタント",
        department: "営業",
        departmentColor: "#0F1627",
        description: "優れた顧客サービスでドイツ設計の製品を世界市場に届ける営業チームをサポートする。",
        requirements: [
          "営業またはカスタマーサービスの経験",
          "優れたコミュニケーションと対人スキル",
          "CRMソフトウェアの熟練",
          "目標指向のマインドセット",
          "速いペースの環境で働く能力"
        ],
        benefits: [
          "コミッションベースのインセンティブ",
          "キャリアアップの機会",
          "包括的なトレーニングプログラム",
          "チームビルディング活動",
          "国際的な露出"
        ]
      },
      {
        icon: Globe2,
        title: "通信スペシャリスト",
        department: "IT",
        departmentColor: "#0F1627",
        description: "ドイツエンジニアリングの精度と信頼性で通信システムを維持・最適化する。",
        requirements: [
          "通信または関連分野の学士号",
          "通信における3年以上の経験",
          "VoIP、ネットワークプロトコル、インフラの知識",
          "通信システムの認定",
          "問題解決と分析スキル"
        ],
        benefits: [
          "競争力のある給与パッケージ",
          "継続的な学習機会",
          "健康とウェルネスプログラム",
          "最先端技術へのアクセス",
          "リモートワークの選択肢"
        ]
      },
      {
        icon: BarChart2,
        title: "ソーシャルマーケティングプロフェッショナル",
        department: "マーケティング",
        departmentColor: "#0F1627",
        description: "「メイド・イン・ジャーマニー」ブランドを世界の観客に示すソーシャルメディア戦略を開発・実行する。",
        requirements: [
          "マーケティングまたは関連分野の学士号",
          "ソーシャルメディアマーケティングで2年以上の経験",
          "ソーシャルメディアプラットフォームと分析の知識",
          "クリエイティブなコンテンツ作成スキル",
          "デジタルマーケティングトレンドの理解"
        ],
        benefits: [
          "競争力のある報酬",
          "クリエイティブな職場環境",
          "プロフェッショナルな成長の機会",
          "柔軟な勤務形態",
          "国際的なマーケティング露出"
        ]
      },
      {
        icon: LineChart,
        title: "検索エンジンマーケティング",
        department: "マーケティング",
        departmentColor: "#0F1627",
        description: "ドイツエンジニアリングの卓越性を定義する精度と効率でオンライン存在を最適化する。",
        requirements: [
          "SEO/SEM戦略の経験",
          "Google AnalyticsとAdWordsの知識",
          "キーワードリサーチと最適化の理解",
          "データ分析とレポートスキル",
          "アルゴリズム変更への適応能力"
        ],
        benefits: [
          "パフォーマンスベースのボーナス",
          "プロフェッショナル開発予算",
          "チームコラボレーションの機会",
          "ワークライフバランスへの注力",
          "モダンなオフィス環境"
        ]
      },
      {
        icon: Briefcase,
        title: "ビジネスアナリスト",
        department: "ビジネス",
        departmentColor: "#0F1627",
        description: "「メイド・イン・ジャーマニー」の卓越性アプローチを体現し、ビジネスプロセスに分析的精度を適用する。",
        requirements: [
          "経営管理または関連分野の学士号",
          "ビジネス分析で3年以上の経験",
          "強力な分析と問題解決スキル",
          "データ分析ツールの熟練",
          "優れたコミュニケーション能力"
        ],
        benefits: [
          "競争力のある給与パッケージ",
          "キャリア開発の機会",
          "健康福利",
          "ワークライフバランスの取り組み",
          "定期的なチームビルディングイベント"
        ]
      },
      {
        icon: Layout,
        title: "フロントエンド開発者",
        department: "エンジニアリング",
        departmentColor: "#0F1627",
        description: "ドイツデザインの精度と美的品質で直感的かつ効率的なユーザーインターフェースを作成する。",
        requirements: [
          "現代のJavaScriptフレームワーク（React、Vue、Angular）の経験",
          "強力なHTML5およびCSS3スキル",
          "レスポンシブデザイン原則の理解",
          "UI/UXベストプラクティスの知識",
          "バージョン管理システムの経験"
        ],
        benefits: [
          "競争力のある報酬",
          "リモートワークの選択肢",
          "プロフェッショナル開発リソース",
          "協働的な職場環境",
          "最新技術スタック"
        ]
      },
      {
        icon: Server,
        title: "バックエンド開発者",
        department: "エンジニアリング",
        departmentColor: "#0F1627",
        description: "ドイツエンジニアリングを定義する信頼性で堅牢かつスケーラブルなサーバーサイドアプリケーションを開発する。",
        requirements: [
          "バックエンド言語（Java、Python、Node.js）の熟練",
          "データベース（SQL、NoSQL）の経験",
          "RESTful APIデザインの知識",
          "サーバーアーキテクチャの理解",
          "問題解決能力"
        ],
        benefits: [
          "競争力のある給与",
          "柔軟な労働時間",
          "健康保険のカバー",
          "プロフェッショナルな成長の機会",
          "革新的なプロジェクトへの参加"
        ]
      },
      {
        icon: Briefcase,
        title: "ITプロジェクトマネージャー",
        department: "マネジメント",
        departmentColor: "#0F1627",
        description: "「メイド・イン・ジャーマニー」基準を体現する精密な計画と実行でITプロジェクトをリードする。",
        requirements: [
          "ITまたは関連分野の学士号",
          "ITプロジェクト管理で5年以上の経験",
          "PMPまたは同等の認定",
          "強力なリーダーシップとコミュニケーションスキル",
          "プロジェクト管理手法の経験"
        ],
        benefits: [
          "競争力のある給与パッケージ",
          "リーダーシップ開発の機会",
          "包括的な健康福利",
          "柔軟な勤務形態",
          "国際的なプロジェクト露出"
        ]
      },
      {
        icon: ShieldCheck,
        title: "システム管理者",
        department: "IT",
        departmentColor: "#0F1627",
        description: "ドイツエンジニアリングの信頼性と精度でITインフラを維持・保護する。",
        requirements: [
          "ITまたは関連分野の学士号",
          "システム管理で3年以上の経験",
          "ネットワークセキュリティプロトコルの知識",
          "クラウドサービス（AWS、Azure、GCP）の経験",
          "問題解決能力"
        ],
        benefits: [
          "競争力のある報酬",
          "プロフェッショナル認定のサポート",
          "健康保険",
          "ワークライフバランスへの注力",
          "継続的な学習機会"
        ]
      }
    ],
    viewJobDetails: "詳細を見る",
    applyNow: "今すぐ応募",
    showMore: "もっと見る",
    showLess: "少なく見る",
    jobRequirements: "要件",
    jobBenefits: "利点"
  },
  kr: {
    mainHeading: "우리 팀에 합류하세요",
    subHeading: "독일 엔지니어링의 우수함으로 경력을 쌓으세요",
    jobPositions: [
      {
        icon: Code,
        title: "소프트웨어 엔지니어",
        department: "엔지니어링",
        departmentColor: "#0F1627",
        description: "'Made in Germany' 정밀 표준을 구현하는 정교한 소프트웨어 솔루션을 설계하고 개발합니다.",
        requirements: [
          "컴퓨터 과학 또는 관련 분야의 학사 학위",
          "소프트웨어 개발 경력 3년 이상",
          "Java, Python 또는 C++에 능숙",
          "소프트웨어 디자인 패턴 및 아키텍처 지식",
          "애자일 개발 방법론 경험"
        ],
        benefits: [
          "경쟁력 있는 급여 및 성과 보너스",
          "포괄적인 건강 보험",
          "전문 개발 기회",
          "유연한 근무 조건",
          "협력적이고 혁신적인 업무 환경"
        ]
      },
      {
        icon: Megaphone,
        title: "영업 보조",
        department: "영업",
        departmentColor: "#0F1627",
        description: "탁월한 고객 서비스로 독일 설계 제품을 글로벌 시장에 제공하는 영업 팀을 지원합니다.",
        requirements: [
          "영업 또는 고객 서비스 경험",
          "강력한 커뮤니케이션 및 대인 관계 기술",
          "CRM 소프트웨어에 능숙",
          "목표 지향적 사고방식",
          "빠른 속도의 환경에서 일할 수 있는 능력"
        ],
        benefits: [
          "커미션 기반 인센티브",
          "경력 발전 기회",
          "종합적인 훈련 프로그램",
          "팀 빌딩 활동",
          "국제적 노출"
        ]
      },
      {
        icon: Globe2,
        title: "통신 전문가",
        department: "IT",
        departmentColor: "#0F1627",
        description: "독일 엔지니어링의 정밀성과 신뢰성으로 통신 시스템을 유지하고 최적화합니다.",
        requirements: [
          "통신 또는 관련 분야의 학사 학위",
          "통신 분야 3년 이상 경력",
          "VoIP, 네트워크 프로토콜 및 인프라 지식",
          "통신 시스템 인증",
          "문제 해결 및 분석 기술"
        ],
        benefits: [
          "경쟁력 있는 급여 패키지",
          "지속적인 학습 기회",
          "건강 및 웰니스 프로그램",
          "최첨단 기술 노출",
          "원격 근무 옵션"
        ]
      },
      {
        icon: BarChart2,
        title: "소셜 마케팅 전문가",
        department: "마케팅",
        departmentColor: "#0F1627",
        description: "'Made in Germany' 브랜드를 글로벌 관객에게 보여주는 소셜 미디어 전략을 개발하고 실행합니다.",
        requirements: [
          "마케팅 또는 관련 분야의 학사 학위",
          "소셜 미디어 마케팅 2년 이상 경력",
          "소셜 미디어 플랫폼 및 분석 지식",
          "창의적인 콘텐츠 제작 기술",
          "디지털 마케팅 트렌드 이해"
        ],
        benefits: [
          "경쟁력 있는 보상",
          "창의적인 업무 환경",
          "전문 성장 기회",
          "유연한 근무 조건",
          "국제 마케팅 노출"
        ]
      },
      {
        icon: LineChart,
        title: "검색 엔진 마케팅",
        department: "마케팅",
        departmentColor: "#0F1627",
        description: "독일 엔지니어링 우수성을 정의하는 정밀성과 효율성으로 온라인 존재를 최적화합니다.",
        requirements: [
          "SEO/SEM 전략 경험",
          "Google Analytics 및 AdWords 지식",
          "키워드 조사 및 최적화 이해",
          "데이터 분석 및 보고 기술",
          "알고리즘 변화에 적응하는 능력"
        ],
        benefits: [
          "성과 기반 보너스",
          "전문 개발 예산",
          "팀 협업 기회",
          "업무와 삶의 균형 중시",
          "현대적인 사무실 환경"
        ]
      },
      {
        icon: Briefcase,
        title: "비즈니스 분석가",
        department: "비즈니스",
        departmentColor: "#0F1627",
        description: "'Made in Germany' 접근 방식을 구현하여 비즈니스 프로세스에 분석적 정밀성을 적용합니다.",
        requirements: [
          "경영학 또는 관련 분야의 학사 학위",
          "비즈니스 분석 경력 3년 이상",
          "강력한 분석 및 문제 해결 기술",
          "데이터 분석 도구에 능숙",
          "탁월한 커뮤니케이션 능력"
        ],
        benefits: [
          "경쟁력 있는 급여 패키지",
          "경력 개발 기회",
          "건강 혜택",
          "업무와 삶의 균형 이니셔티브",
          "정기적인 팀 빌딩 행사"
        ]
      },
      {
        icon: Layout,
        title: "프론트엔드 개발자",
        department: "엔지니어링",
        departmentColor: "#0F1627",
        description: "독일 디자인의 정밀성과 미적 품질로 직관적이고 효율적인 사용자 인터페이스를 만듭니다.",
        requirements: [
          "현대 JavaScript 프레임워크(React, Vue, Angular) 경험",
          "강력한 HTML5 및 CSS3 기술",
          "반응형 디자인 원칙 이해",
          "UI/UX 모범 사례 지식",
          "버전 관리 시스템 경험"
        ],
        benefits: [
          "경쟁력 있는 보상",
          "원격 근무 옵션",
          "전문 개발 자원",
          "협력적인 업무 환경",
          "최신 기술 스택"
        ]
      },
      {
        icon: Server,
        title: "백엔드 개발자",
        department: "엔지니어링",
        departmentColor: "#0F1627",
        description: "독일 엔지니어링을 정의하는 신뢰성으로 견고하고 확장 가능한 서버 측 애플리케이션을 개발합니다.",
        requirements: [
          "백엔드 언어(Java, Python, Node.js)에 능숙",
          "데이터베이스(SQL, NoSQL) 경험",
          "RESTful API 디자인 지식",
          "서버 아키텍처 이해",
          "문제 해결 능력"
        ],
        benefits: [
          "경쟁력 있는 급여",
          "유연한 근무 시간",
          "건강 보험 혜택",
          "전문 성장 기회",
          "혁신적인 프로젝트 노출"
        ]
      },
      {
        icon: Briefcase,
        title: "IT 프로젝트 매니저",
        department: "관리",
        departmentColor: "#0F1627",
        description: "'Made in Germany' 표준을 구현하는 정밀한 계획과 실행으로 IT 프로젝트를 이끕니다.",
        requirements: [
          "IT 또는 관련 분야의 학사 학위",
          "IT 프로젝트 관리 경력 5년 이상",
          "PMP 또는 동등한 인증",
          "강력한 리더십 및 커뮤니케이션 기술",
          "프로젝트 관리 방법론 경험"
        ],
        benefits: [
          "경쟁력 있는 급여 패키지",
          "리더십 개발 기회",
          "종합적인 건강 혜택",
          "유연한 근무 조건",
          "국제 프로젝트 노출"
        ]
      },
      {
        icon: ShieldCheck,
        title: "시스템 관리자",
        department: "IT",
        departmentColor: "#0F1627",
        description: "독일 엔지니어링의 신뢰성과 정밀성으로 IT 인프라를 유지하고 보호합니다.",
        requirements: [
          "IT 또는 관련 분야의 학사 학위",
          "시스템 관리 경력 3년 이상",
          "네트워크 보안 프로토콜 지식",
          "클라우드 서비스(AWS, Azure, GCP) 경험",
          "문제 해결 능력"
        ],
        benefits: [
          "경쟁력 있는 보상",
          "전문 인증 지원",
          "건강 보험",
          "업무와 삶의 균형 중시",
          "지속적인 학습 기회"
        ]
      }
    ],
    viewJobDetails: "자세히 보기",
    applyNow: "지금 지원하기",
    showMore: "더 보기",
    showLess: "덜 보기",
    jobRequirements: "요구사항",
    jobBenefits: "혜택"
  },
  sa: {
    mainHeading: "انضم إلى فريقنا",
    subHeading: "ابنِ حياتك المهنية مع التميز الهندسي الألماني",
    jobPositions: [
      {
        icon: Code,
        title: "مهندس برمجيات",
        department: "الهندسة",
        departmentColor: "#0F1627",
        description: "صمم وطور حلول برمجية متطورة تجسد معايير الدقة 'صنع في ألمانيا'.",
        requirements: [
          "درجة البكالوريوس في علوم الحاسوب أو مجال ذي صلة",
          "3+ سنوات من الخبرة في تطوير البرمجيات",
          "إتقان Java أو Python أو C++",
          "معرفة بأنماط تصميم البرمجيات والهندسة المعمارية",
          "خبرة مع منهجيات التطوير الرشيقة"
        ],
        benefits: [
          "راتب تنافسي ومكافآت الأداء",
          "تأمين صحي شامل",
          "فرص التطوير المهني",
          "ترتيبات عمل مرنة",
          "بيئة عمل تعاونية ومبتكرة"
        ]
      },
      {
        icon: Megaphone,
        title: "مساعد مبيعات",
        department: "المبيعات",
        departmentColor: "#0F1627",
        description: "ادعم فريق المبيعات لدينا في تقديم المنتجات الهندسية الألمانية إلى الأسواق العالمية مع خدمة عملاء استثنائية.",
        requirements: [
          "خبرة سابقة في المبيعات أو خدمة العملاء",
          "مهارات تواصل قوية ومهارات بينية",
          "إتقان برامج CRM",
          "عقلية موجهة نحو الأهداف",
          "القدرة على العمل في بيئة سريعة الخطى"
        ],
        benefits: [
          "حوافز قائمة على العمولة",
          "فرص التقدم الوظيفي",
          "برنامج تدريب شامل",
          "أنشطة بناء الفريق",
          "التعرض الدولي"
        ]
      },
      {
        icon: Globe2,
        title: "أخصائي الاتصالات",
        department: "تكنولوجيا المعلومات",
        departmentColor: "#0F1627",
        description: "حافظ على أنظمة الاتصالات لدينا وتحسينها بدقة وموثوقية الهندسة الألمانية.",
        requirements: [
          "درجة البكالوريوس في الاتصالات أو مجال ذي صلة",
          "3+ سنوات من الخبرة في الاتصالات",
          "معرفة بـ VoIP وبروتوكولات الشبكة والبنية التحتية",
          "شهادة في أنظمة الاتصالات",
          "مهارات حل المشكلات والتحليل"
        ],
        benefits: [
          "حزمة رواتب تنافسية",
          "فرص التعلم المستمر",
          "برامج الصحة والعافية",
          "التعرض لتكنولوجيا متطورة",
          "خيارات العمل عن بُعد"
        ]
      },
      {
        icon: BarChart2,
        title: "محترفو التسويق الاجتماعي",
        department: "التسويق",
        departmentColor: "#0F1627",
        description: "طوّر ونفذ استراتيجيات وسائل التواصل الاجتماعي التي تعرض علامتنا التجارية 'صنع في ألمانيا' لجمهور عالمي.",
        requirements: [
          "درجة البكالوريوس في التسويق أو مجال ذي صلة",
          "2+ سنوات من الخبرة في التسويق عبر وسائل التواصل الاجتماعي",
          "معرفة بمنصات وسائل التواصل الاجتماعي والتحليلات",
          "مهارات إنشاء محتوى إبداعي",
          "فهم اتجاهات التسويق الرقمي"
        ],
        benefits: [
          "تعويض تنافسي",
          "بيئة عمل إبداعية",
          "فرص النمو المهني",
          "ترتيبات عمل مرنة",
          "التعرض للتسويق الدولي"
        ]
      },
      {
        icon: LineChart,
        title: "تسويق محركات البحث",
        department: "التسويق",
        departmentColor: "#0F1627",
        description: "حسن وجودنا عبر الإنترنت بالدقة والكفاءة التي تحدد التميز الهندسي الألماني.",
        requirements: [
          "خبرة في استراتيجيات SEO/SEM",
          "معرفة بـ Google Analytics وAdWords",
          "فهم البحث عن الكلمات المفتاحية وتحسينها",
          "مهارات تحليل البيانات وإعداد التقارير",
          "القدرة على التكيف مع تغيرات الخوارزميات"
        ],
        benefits: [
          "مكافآت قائمة على الأداء",
          "ميزانية التطوير المهني",
          "فرص التعاون الجماعي",
          "التركيز على التوازن بين الحياة العملية والشخصية",
          "بيئة مكتبية حديثة"
        ]
      },
      {
        icon: Briefcase,
        title: "محلل أعمال",
        department: "الأعمال",
        departmentColor: "#0F1627",
        description: "طبق الدقة التحليلية على عمليات الأعمال، مجسداً نهجنا 'صنع في ألمانيا' نحو التميز.",
        requirements: [
          "درجة البكالوريوس في إدارة الأعمال أو مجال ذي صلة",
          "3+ سنوات من الخبرة في تحليل الأعمال",
          "مهارات تحليلية قوية وحل المشكلات",
          "إتقان أدوات تحليل البيانات",
          "مهارات تواصل ممتازة"
        ],
        benefits: [
          "حزمة رواتب تنافسية",
          "فرص التطوير الوظيفي",
          "مزايا صحية",
          "مبادرات التوازن بين الحياة العملية والشخصية",
          "فعاليات بناء الفريق المنتظمة"
        ]
      },
      {
        icon: Layout,
        title: "مطور واجهة أمامية",
        department: "الهندسة",
        departmentColor: "#0F1627",
        description: "أنشئ واجهات مستخدم بديهية وفعالة بدقة وجودة جمالية التصميم الألماني.",
        requirements: [
          "خبرة مع أطر عمل JavaScript الحديثة (React، Vue، Angular)",
          "مهارات قوية في HTML5 وCSS3",
          "فهم مبادئ التصميم التجاوبي",
          "معرفة بأفضل ممارسات UI/UX",
          "خبرة مع أنظمة التحكم في الإصدارات"
        ],
        benefits: [
          "تعويض تنافسي",
          "خيارات العمل عن بُعد",
          "موارد التطوير المهني",
          "بيئة عمل تعاونية",
          "أحدث مكدس تكنولوجي"
        ]
      },
      {
        icon: Server,
        title: "مطور خلفي",
        department: "الهندسة",
        departmentColor: "#0F1627",
        description: "طوّر تطبيقات خلفية قوية وقابلة للتطوير مع الموثوقية التي تحدد الهندسة الألمانية.",
        requirements: [
          "إتقان لغات الخلفية (Java، Python، Node.js)",
          "خبرة مع قواعد البيانات (SQL، NoSQL)",
          "معرفة بتصميم API RESTful",
          "فهم معمارية الخادم",
          "مهارات حل المشكلات"
        ],
        benefits: [
          "راتب تنافسي",
          "ساعات عمل مرنة",
          "تغطية التأمين الصحي",
          "فرص النمو المهني",
          "التعرض لمشاريع مبتكرة"
        ]
      },
      {
        icon: Briefcase,
        title: "مدير مشاريع تكنولوجيا المعلومات",
        department: "الإدارة",
        departmentColor: "#0F1627",
        description: "قد مشاريع تكنولوجيا المعلومات بالتخطيط والتنفيذ الدقيقين اللذين يجسدان معاييرنا 'صنع في ألمانيا'.",
        requirements: [
          "درجة البكالوريوس في تكنولوجيا المعلومات أو مجال ذي صلة",
          "5+ سنوات من الخبرة في إدارة مشاريع تكنولوجيا المعلومات",
          "شهادة PMP أو ما يعادلها",
          "مهارات قيادية وتواصل قوية",
          "خبرة مع منهجيات إدارة المشاريع"
        ],
        benefits: [
          "حزمة رواتب تنافسية",
          "فرص تطوير القيادة",
          "مزايا صحية شاملة",
          "ترتيبات عمل مرنة",
          "التعرض لمشاريع دولية"
        ]
      },
      {
        icon: ShieldCheck,
        title: "مدير النظام",
        department: "تكنولوجيا المعلومات",
        departmentColor: "#0F1627",
        description: "حافظ على بنيتنا التحتية لتكنولوجيا المعلومات وأمنها بموثوقية ودقة الهندسة الألمانية.",
        requirements: [
          "درجة البكالوريوس في تكنولوجيا المعلومات أو مجال ذي صلة",
          "3+ سنوات من الخبرة في إدارة النظام",
          "معرفة ببروتوكولات أمان الشبكة",
          "خبرة مع خدمات الحوسبة السحابية (AWS، Azure، GCP)",
          "مهارات حل المشكلات"
        ],
        benefits: [
          "تعويض تنافسي",
          "دعم الشهادات المهنية",
          "تغطية صحية",
          "التركيز على التوازن بين الحياة العملية والشخصية",
          "فرص التعلم المستمر"
        ]
      }
    ],
    viewJobDetails: "عرض التفاصيل",
    applyNow: "قدم الآن",
    showMore: "عرض المزيد",
    showLess: "عرض أقل",
    jobRequirements: "المتطلبات",
    jobBenefits: "المزايا"
  }
};

const JobCard: React.FC<{ job: JobPosition; isExpanded: boolean; onToggle: () => void; language: string }> = ({
  job,
  isExpanded,
  onToggle,
  language
}) => {
  const Icon = job.icon;
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
  const navigate = useNavigate();

  const handleRecruitingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/recruitingform');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="job-card relative bg-[#0B111F] rounded-2xl p-8 border border-gray-800 backdrop-blur-xl overflow-hidden group flex flex-col"
      >
        <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
        
        <div className="relative flex flex-col space-y-6 z-10">
          <div className="flex items-center space-x-5">
            <motion.div 
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="icon-wrapper bg-[#0C1220] p-3 rounded-xl shadow-lg relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-[#0C1220] opacity-50" />
              <Icon className="w-7 h-7 text-[#FFFFFF] relative z-10" />
            </motion.div>
            <div>
              <span 
                className="text-sm font-semibold px-3 py-1 rounded-[5px] transition-all duration-300 group-hover:shadow-lg" 
                style={{ backgroundColor: '#ffffff', color: '#0B111F' }}
              >
                {job.department}
              </span>
              <h3 className="text-white text-2xl font-bold mt-3 tracking-tight">{job.title}</h3>
            </div>
          </div>
  
          <p className="text-gray-200 leading-relaxed font-medium">{job.description}</p>
  
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? 'auto' : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-6 space-y-6">
              <div>
                <h4 className="text-white font-semibold text-lg mb-3">{translations.jobRequirements}</h4>
                <ul className="list-none space-y-3">
                  {job.requirements.map((req, index) => (
                    <motion.li 
                      key={index}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="text-gray-300 flex items-center"
                    >
                      <span className="w-2 h-2 bg-gray-500 rounded-full mr-3" />
                      {req}
                    </motion.li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold text-lg mb-3">{translations.jobBenefits}</h4>
                <ul className="list-none space-y-3">
                  {job.benefits.map((benefit, index) => (
                    <motion.li 
                      key={index}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="text-gray-300 flex items-center"
                    >
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                      {benefit}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
  
          <div className="flex flex-col sm:flex-row gap-4 pt-6 mt-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-lg bg-white text-[#0B111F] font-semibold shadow-lg transition-all duration-300 hover:bg-gray-100 flex-1"
              onClick={handleRecruitingClick}
            >
              {translations.applyNow}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-lg bg-transparent text-white font-semibold border-2 border-white transition-all duration-300 hover:bg-white hover:text-[#0B111F] flex-1 flex items-center justify-center"
              onClick={onToggle}
            >
              {isExpanded ? translations.showLess : translations.viewJobDetails}
              {isExpanded ? <ChevronUp className="ml-2 w-5 h-5" /> : <ChevronDown className="ml-2 w-5 h-5" />}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  };
  
  // CareerPage Component
  const CareerPage: React.FC<CareerProps> = ({ language = 'en' }) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];
  
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#FFFFFF] py-10">
        <div className="max-w-7xl mx-auto px-0">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16 relative"
          >
            <h1 className="text-5xl md:text-6xl font-extrabold text-[#0B111F] mb-6 flex items-center justify-center gap-3 flex-wrap">
              {translations.mainHeading}
              <svg className="w-8 h-8 text-[#0B111F]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <svg className="w-8 h-8 text-[#0B111F]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <svg className="w-8 h-8 text-[#0B111F]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <svg className="w-8 h-8 text-[#0B111F]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              <svg className="w-8 h-8 text-[#0B111F]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </h1>
            <div className="absolute inset-0 -z-10 bg-black blur-3xl" />
          </motion.div>
  
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {translations.jobPositions.map((job, index) => (
              <JobCard
                key={index}
                job={job}
                isExpanded={index === expandedIndex}
                onToggle={() => setExpandedIndex(index === expandedIndex ? null : index)}
                language={language}
              />
            ))}
          </motion.div>
        </div>
      </div>
    );
  };
  
  export default CareerPage;